// Dashboard-only translations (landing page has its own i18n in /tb/i18n.js)
export const dict = {
  en: {
    appName: "TractionBoard",
    nav: {
      dashboard: "Bookings",
      journal: "Journal",
      brain: "Brain",
      sessions: "Sessions",
      newSession: "Book a session",
      clients: "Clients",
      community: "Community",
      portfolio: "Portfolio",
      framework: "Frameworks",
      metrics: "Metrics",
      roadmap: "Roadmap",
      toolbox: "Toolbox",
      mandates: "Mandates",
      logout: "Log out",
    },
    menu: {
      profile: "Profile",
      sendFeedback: "Send feedback",
      surveys: "Surveys",
      whatsNext: "What's next",
      changePassword: "Change password",
      language: "Language",
      signOut: "Sign out",
      adminConsole: "Admin console",
    },
    adminConsole: {
      title: "Admin · TractionBoard",
      subtitle: "Internal dashboard",
      refresh: "Refresh",
      byRoleTitle: "Users by role",
      tabs: { overview: "Overview", feedback: "Feedback", users: "Users", roadmap: "Roadmap", settings: "Settings", waitlist: "Waitlist", flows: "Flow" },
      kpi: {
        totalUsers: "Total users",
        wau7: "WAU (7d)",
        totalSessions: "Sessions",
        activeMandates: "Active mandates",
        connections: "Connections",
        newFeedback: "New feedback",
        scheduled: "scheduled",
        completed: "completed",
        total: "total",
        allTime: "all time",
      },
      feedback: {
        allStatuses: "All statuses",
        bug: "Bug",
        feature: "Feature",
        note: "Note",
        empty: "No feedback matches these filters.",
        status: {
          new: "new",
          reviewed: "reviewed",
          planned: "planned",
          fixed: "fixed",
          ignored: "ignored",
        },
        promote: "Promote",
        promoteHint: "Add this feedback as a new item on the public roadmap",
        promoteConfirm: "Promote this feedback to the public roadmap?",
        promoteDone: "Added to roadmap as 'Considering'. You can drag it forward in the Roadmap tab.",
      },
      settings: {
        loading: "Loading…",
        confirmDisconnect: "Disconnect the TractionBoard system calendar? New bookings will fall back to manual meeting links until reconnected.",
        systemCalendar: {
          title: "TractionBoard system calendar",
          description: "Connect a TractionBoard-owned Google Workspace account (e.g. calendar@tractionboardai.com). When a booking creator hasn't connected their own Google account, this system account auto-generates a Google Meet link so every session gets a working video link and a Recall.ai transcript.",
          connected: "Connected",
          notConnected: "Not connected",
          since: "Connected since",
          connect: "Connect Google account",
          connecting: "Redirecting to Google…",
          disconnect: "Disconnect",
          howItWorks: "How it works",
          step1: "If the advisor/creator has Google connected → the event lives on their calendar (existing behaviour).",
          step2: "Otherwise → TractionBoard creates the event on this system account and a Meet link is auto-generated.",
          step3: "The ICS invite is always sent from TractionBoard in parallel, so non-Google users get the event too.",
        },
      },
    },
    adminWaitlist: {
      modeTitle: "Signup mode",
      modeHelp: "Waitlist mode captures new signups as invite requests you must approve. Open mode lets anyone sign up directly.",
      modeWaitlist: "Invite-only (waitlist)",
      modeOpen: "Open (public signup)",
      mode_waitlist: "Invite-only",
      mode_open: "Open",
      modeCurrent: "Current mode: {mode}",
      status_pending: "Pending",
      status_approved: "Approved",
      status_rejected: "Rejected",
      empty: "No requests here.",
      approve: "Approve",
      reject: "Reject",
      confirmApprove: "Approve this access request? An invite email will be sent so they can set their password.",
      confirmReject: "Reject this access request?",
      col: { name: "Name", email: "Email", role: "Role", company: "Company", note: "Note", created: "Requested" },
    },
    adminFlows: {
      intro: "Generic product flows (not a named cohort). Each step shows the view (founder, accelerator, advisor, admin, public). Lab logins: founder@test.tb / Test12345! — same password for cofounder, accelerator, coach. Notes stay in this browser.",
      signup: "Sign up",
      adminApprove: "Admin approve (waitlist)",
      emailLink: "Click the email link to set password",
      setPassword: "Set the new password",
      loginForgot: "Login / forgot password",
      arrival: "Arrival on TractionBoard",
      recs: "Recommendations (Must / Nice to have)",
      stepNotes: "Capture gaps, bugs, wording…",
      screenshotTbd: "Screenshot TBD",
      emailStub: "Local: RESEND_API_KEY empty → EMAIL stub in backend log, no inbox.",
      workingNotes: "Working notes / bugs",
    },
    whatsNext: {
      publicTitle: "What's next on TractionBoard",
      publicIntro: "Here's what we're working on right now, what's coming up, and what we shipped recently. Built largely thanks to your feedback.",
      publicEmpty: "No public roadmap items yet. Check back soon!",
      publicCta: "Have an idea or hit a bug?",
      sendFeedback: "Send us feedback",
      col: {
        considering: "Considering",
        up_next: "Up next",
        in_progress: "In progress",
        shipped: "Shipped",
      },
      adminHint: "Drag cards between columns to update status. 'Considering' is hidden from public.",
      addItem: "Add item",
      titlePlaceholder: "What are we building?",
      descPlaceholder: "Optional details (visible to users when published)…",
      emptyCol: "Empty",
      linkedFeedback: "linked",
      confirmDelete: "Delete this roadmap item permanently?",
    },
    comingSoon: {
      backToToolbox: "Back to Toolbox",
    },
    feedback: {
      title: "Send feedback",
      intro: "TractionBoard is in beta — your input shapes it. Tell us what's working, what's broken, what you'd love.",
      ratingLabel: "How's your experience so far?",
      tapStar: "Tap a star",
      rating1: "Frustrating",
      rating2: "Could be better",
      rating3: "It's okay",
      rating4: "Pretty good",
      rating5: "Love it",
      bugLabel: "Found a bug?",
      bugPlaceholder: "Describe what went wrong…",
      featureLabel: "Feature request?",
      featurePlaceholder: "What would make TractionBoard better for you?",
      anythingLabel: "Anything else?",
      anythingPlaceholder: "Share your thoughts, encouragement, or anything…",
      submit: "Send feedback",
      cancel: "Cancel",
      imageLabel: "Attach screenshots",
      imageHelp: "Optional. Up to two pictures, 3MB each.",
      attachImage: "Attach images",
      removeImage: "Remove",
      imageTooBig: "Image too large (max 3MB)",
      tooManyImages: "You can attach two images.",
      imageOnly: "Choose an image file.",
      videoHelp: "Optional. One screen recording, up to 8MB.",
      attachVideo: "Attach a screen recording",
      videoOnly: "Choose a video file.",
      videoTooBig: "Recording too large (max 8MB)",
      footnote: "Linked to your account so we can follow up if needed.",
      thanks: "Thanks!",
      thanksBody: "Your feedback just landed in our inbox.",
    },
    auth: {
      loginTitle: "Log in",
      registerTitle: "Create your account",
      email: "Email",
      password: "Password",
      name: "Full name",
      company: "Company",
      role: "I am a…",
      roleEntrepreneur: "Founder / Entrepreneur",
      roleAdvisor: "Advisor",
      roleProvider: "Service provider",
      roleInvestor: "Fund manager",
      roleAccelerator: "Accelerator",
      lang: "Language",
      langEn: "English",
      langFr: "Français",
      submitLogin: "Log in",
      submitRegister: "Create account",
      submitRequestAccess: "Request access",
      switchToRegister: "Don't have an account? Sign up",
      switchToLogin: "Already have an account? Log in",
      inviteOnlyNote: "TractionBoard is currently invite-only. Fill out the form below and an admin will review your request.",
      noteLabel: "Why do you want to join? (optional)",
      notePlaceholder: "Tell us a bit about you, your company, or what brings you to TractionBoard.",
      waitlistTitle: "You're on the waitlist",
      waitlistBody: "Thanks — we've received your request from {email}. TractionBoard is currently invite-only. An admin will review it and email you when your account is approved.",
      waitlistAlready: "You'd already requested access — we've kept your existing request.",
      consentRequired: "Please accept all three agreements to continue.",
      consentTermsPrefix: "I have read and agree to the",
      consentTermsLink: "Terms of Service",
      consentTermsSuffix: ".",
      consentAnd: "and the",
      consentPrivacyPrefix: "I have read and agree to the",
      consentPrivacyLink: "Privacy Policy",
      consentPrivacySuffix: ".",
      consentBetaPrefix: "I understand that TractionBoard is a private beta and agree to the",
      consentBetaLink: "Beta Program Terms",
      consentBetaSuffix: ".",
      loading: "Please wait…",
      forgotLink: "Forgot password?",
      forgotTitle: "Reset your password",
      forgotHelp: "Enter your account email and we'll send you a reset link.",
      sendResetLink: "Send reset link",
      checkEmailTitle: "Check your email",
      checkEmailBody: "If an account exists with that email, a reset link is on its way. It expires in 1 hour.",
      backToLogin: "Back to login",
      resetTitle: "Set a new password",
      newPassword: "New password",
      confirmPassword: "Confirm password",
      setNewPassword: "Update password",
      passwordsDontMatch: "Passwords don't match.",
      passwordTooShort: "Password must be at least 6 characters.",
      resetDoneTitle: "Password updated",
      resetDoneBody: "You can now log in with your new password.",
      goToLogin: "Log in",
    },
    dash: {
      greeting: "Hi",
      upcoming: "Upcoming sessions",
      past: "Past sessions",
      noUpcoming: "No upcoming sessions yet.",
      noPast: "No past sessions yet.",
      book: "Book a meeting",
      bookMandate: "Book a mandate",
      noMandates: "No mandates yet.",
      activeMandates: "Active mandates",
      pastMandates: "Past mandates",
      noActiveMandates: "No active mandates.",
      noPastMandates: "No past mandates.",
      advisorCTA: "Check your agenda & prep",
      observer: "You will see sessions where you're invited as an observer.",
      viewAll: "View all",
      createMandate: "Create a mandate",
      acceleratorHint: "Sessions of connected founders appear here. Shared notes, transcripts and AI summaries are read-only.",
      filterAllFounders: "All founders",
      filterAllAdvisors: "All advisors",
      filterAllPrograms: "All programs",
      filterAllCohorts: "All cohorts",
      filterAllYears: "All years",
    },
    team1v1: {
      bookButton: "Book teammate",
      bookTitle: "Schedule a teammate meeting",
      with: "With",
      pickTeammate: "Choose a teammate",
      noTeammates: "Invite a teammate first — these meetings are for people on your company team.",
      template: "Template",
      pickTemplate: "Choose a template",
      manageTemplates: "Manage templates",
      notes: "Notes (optional)",
      schedule: "Schedule meeting",
      agenda: "Before meeting",
      myAgenda: "My prep",
      theirAgenda: "Their prep",
      submitAgenda: "Submit prep",
      agendaSubmitted: "Submitted {date}",
      waitingForThem: "Waiting for them to submit their prep.",
      emptyState: "Schedule your first teammate meeting",
      summary: "AI summary",
      generateSummary: "Generate summary",
      summaryNotYet: "Summary appears after both agendas and the transcript — or generate it now.",
      topics: "Key topics",
      decisions: "Decisions and commitments",
      followUps: "Follow-ups for next meeting",
      sentiment: "Sentiment signals",
      detailTitle: "Meeting with {name}",
      badge: { advisory: "Session", internal_1_1: "Teammate", mandate: "Mandate" },
      withPerson: "with {name}",
      duringNotes: "During-meeting notes",
      duringNotesHint: "Choose who can see each note: everyone in the meeting, the accelerator, or only you. Private notes never go into the AI summary.",
      noNotes: "No notes yet.",
      shared: "Shared with everyone",
      sharedAccelerator: "Shared with accelerator",
      private: "Private",
      makePrivate: "Make private",
      makeShared: "Share with everyone",
      makeSharedAccelerator: "Share with accelerator",
      notePlaceholder: "Capture what is being said or decided…",
      addNote: "Add note",
      afterForm: "After meeting",
      myAfter: "My wrap-up",
      theirAfter: "Their wrap-up",
      submitAfter: "Submit wrap-up",
      waitingAfter: "Waiting for their wrap-up.",
    },
    sessions: {
      title: "Sessions",
      scheduled: "Scheduled",
      completed: "Completed",
      withAdvisor: "with",
      open: "Open",
      status: "Status",
      preDone: "Pre-form filled",
      preMissing: "Pre-form pending",
      postDone: "Post-form filled",
      summaryReady: "Summary ready",
      recapPending: "Recap pending",
      filterAll: "All",
      filterUpcoming: "Upcoming",
      filterPast: "Past",
      viewArchive: "Archive",
      backToActive: "Back to active",
      archiveEmpty: "No archived sessions yet.",
    },
    book: {
      title: "Book a meeting",
      with: "With",
      pickPerson: "Choose who to meet",
      noPeople: "Connect with someone first via Community.",
      advisor: "Advisor",
      entrepreneur: "Entrepreneur",
      pickAdvisor: "Pick an advisor",
      pickEntrepreneur: "Pick an entrepreneur",
      pickFounder: "Pick a founder",
      colleague: "Colleague",
      pickColleague: "Pick a colleague (optional)",
      noColleagues: "No colleague connected yet",
      other: "Other",
      pickOther: "Pick someone else (optional)",
      noOthers: "No other connections yet",
      noAdvisors: "No advisor available yet",
      noEntrepreneurs: "No entrepreneur registered yet",
      noFounders: "Connect with a founder first via Community.",
      when: "When",
      duration: "Duration (min)",
      recurrence: "Repeat",
      recurrenceNone: "Does not repeat",
      recurrenceDaily: "Daily",
      recurrenceWeekly: "Weekly",
      recurrenceWeeklyOn: "Weekly on {day}",
      recurrenceMonthlyOnNth: "Monthly on the {nth} {day}",
      recurrenceMonthlyOnDay: "Monthly on day {n}",
      recurrenceYearlyOn: "Annually on {date}",
      recurrenceWeekdays: "Every weekday (Monday to Friday)",
      recurrenceCustom: "Custom…",
      recurrenceCount: "Number of meetings",
      recurrenceEvery: "Repeat every",
      recurrenceUnitDay: "day",
      recurrenceUnitDays: "days",
      recurrenceUnitWeek: "week",
      recurrenceUnitWeeks: "weeks",
      recurrenceUnitMonth: "month",
      recurrenceUnitMonths: "months",
      recurrenceUnitYear: "year",
      recurrenceUnitYears: "years",
      recurrenceOnDays: "Repeat on",
      recurrenceMonthlyMode: "Monthly pattern",
      recurrenceEnds: "Ends",
      recurrenceEndsAfter: "After a number of meetings",
      recurrenceEndsOn: "On a date",
      recurrenceUntil: "End date",
      dowPlaceholder: "that day",
      nth: { first: "first", second: "second", third: "third", fourth: "fourth", last: "last" },
      dowShort: { 0: "S", 1: "M", 2: "T", 3: "W", 4: "T", 5: "F", 6: "S" },
      sessionTitle: "Title (optional)",
      template: "Meeting template",
      rate: "Rate (per session)",
      currency: "Currency",
      meetingLink: "Meeting link (optional, can add later)",
      meetingLinkHelp: "Paste a Zoom, Teams, Webex or Meet URL. If you've connected Google Calendar, a Meet link will auto-generate. The auto-recording bot works on all 4 platforms.",
      observers: "Invite observers (optional)",
      observersHelp: "Providers, investors, or accelerator partners with read-only access.",
      cancel: "Cancel",
      create: "Create session",
    },
    settings: {
      title: "Settings",
      subtitle: "Manage your profile and account credentials.",
      profileTitle: "Profile",
      bio: "Bio",
      bioPlaceholder: "A short description that advisors and founders will see.",
      photoUrl: "Photo",
      photoUpload: "Upload a photo",
      photoRemove: "Remove photo",
      photoSelected: "Uploaded photo",
      photoTooBig: "Choose an image under 8 MB.",
      photoNotImage: "Choose an image file.",
      headline: "Current title",
      headlinePlaceholder: "e.g. Growth and Strategy Consultant at Colectiv",
      location: "Place",
      locationPlaceholder: "City, country",
      season: "Date",
      linkedin: "LinkedIn page",
      website: "Website",
      websitePlaceholder: "https://your-site.com",
      tagline: "Short tagline",
      taglinePlaceholder: "e.g. Entrepreneur | Advisor | Tech, AI & Biotech",
      importLinkedInPhoto: "Import LinkedIn photo",
      importingPhoto: "Importing…",
      photoImported: "Photo imported from LinkedIn",
      photoHow: "Upload an image, or open the photo in a separate browser tab, copy the image address, and paste that URL below.",
      expertiseTitle: "Areas of expertise",
      expertiseHint: "Pick the domains you advise on. These tags appear on your community card.",
      bioLangHint: "On save, empty fields in the other language are translated. You can edit the result afterwards.",
      profileText: "Title, tagline and biography",
      appLang: "Interface language",
      appLangHint: "Language of the app and emails after you save.",
      expertiseDetailLabel: "Details — {area}",
      expertiseDetailPlaceholder: "One capability per line, e.g. Strategic planning and roadmap",
      mediaMentions: "In the media",
      mediaMentionsPlaceholder: "Press mentions, talks, or notable coverage (optional)",
      extraLinks: "More links",
      extraLinksHint: "Optional extra URLs shown at the end of your public profile.",
      linkLabel: "Label",
      addLink: "Add a link",
      removeLink: "Remove",
      composeTitle: "Compose from LinkedIn",
      composeHint: "Paste your LinkedIn profile URL to fill the photo, name, title, about, company, and location.",
      composeWelcome: "Welcome. Start with your LinkedIn URL and we’ll draft your advisor profile.",
      composeApply: "Fill from LinkedIn",
      composing: "Reading LinkedIn…",
      composePartial: "Photo and name were filled. LinkedIn hid the public title and about — add those below.",
      composeLimited: "LinkedIn’s public card includes the photo, name, company, location, and a short about. It cuts the about short and does not include Current title or Short tagline — add those two below.",
      directoryToggle: "List me in the community directory",
      directoryHint: "When enabled, other users can find your public profile (name, role, company, bio) under \"All\" on the Community page.",
      directoryTitle: "Community visibility",
      directoryPublic: "Public — list me in the directory",
      directoryPublicHint: "Any TractionBoard user can find you under the Community page.",
      directoryWhitelist: "Selective — list me only to these emails",
      directoryWhitelistHint: "Only users with these email addresses see your profile in the directory.",
      directoryHidden: "Hidden — don't list me",
      directoryHiddenHint: "You're only visible to people you're already connected with.",
      save: "Save profile",
      saved: "Saved",
      passwordTitle: "Change password",
      currentPassword: "Current password",
      updatePassword: "Update password",
      passwordUpdated: "Password updated",
      teamTitle: "Co-founders & team",
      teamHint: "Invite teammates who share your company. They get full read/write access to your metrics, roadmap, frameworks and documents.",
      teamTitleAccelerator: "Accelerator colleagues",
      teamHintAccelerator: "Invite colleagues onto the principal accelerator account. They receive an invitation email and can view and edit the company and coach portfolio. Only the principal can import a CSV.",
      manageTeam: "Manage team",
      templates: {
        title: "Meeting templates",
        weekly11Name: "Weekly 1:1",
        hint: "Templates to capture information before, during and after meetings. Starter packs cover advisor, accelerator, investor, provider, and teammate meetings. Duplicate a system template to edit a copy, or import markdown to create a new one.",
        principalOnly: "Advisors, accelerators, investors, providers, admins, or the primary founder can manage meeting templates.",
        empty: "No templates yet — duplicate a system default to start.",
        default: "Default",
        system: "System",
        edit: "Edit",
        duplicate: "Duplicate",
        delete: "Delete",
        new: "+ New template",
        titleNew: "New template",
        titleEdit: "Edit template",
        notFound: "Template not found.",
        systemCopyHint: "System templates can’t be overwritten. Save creates your own copy.",
        label: "Question",
        prompt: "Prompt",
        jumpBasics: "Basics",
        jumpBefore: "Before",
        jumpDuring: "During",
        jumpAfter: "After",
        jumpAi: "AI",
        qtype: {
          long_text: "Long text",
          short_text: "Short text",
          scale_1_10: "Scale 1–10",
          single_choice: "Single choice",
          multi_choice: "Multi choice",
          checklist: "Checklist",
        },
        name: "Name",
        description: "Description",
        markDefault: "Use as default",
        type: "Type",
        options: "Options (comma-separated)",
        addQuestion: "+ Add question",
        removeQuestion: "Remove",
        confirmDelete: "Delete this template? If it has been used, it will be archived instead.",
        manage: "Manage templates",
        required: "Required",
        tabAdvisory: "Advisor",
        tabAccelerator: "Accelerator",
        tabInvestor: "Investor",
        tabProvider: "Provider",
        tabTeammate: "Teammate",
        beforeTitle: "Before-meeting form",
        duringTitle: "During-meeting note prompts",
        duringHint: "Shown above Share / Private notes. Prompts only — they are not extra form fields.",
        addDuringPrompt: "+ Add during-meeting prompt",
        afterTitle: "After-meeting form",
        afterHint: "Wrap-up questions. After is stored separately from before.",
        addAfterQuestion: "+ Add after-meeting question",
        beforeCount: "before",
        duringCount: "during",
        afterCount: "after",
        question: "question",
        questions: "questions",
        whoFounder: "founder",
        whoAdvisor: "advisor",
        whoAccelerator: "accelerator",
        whoInvestor: "investor",
        whoProvider: "provider",
        whoBoth: "both",
        whoEach: "each",
        countBit: "{n} {word} {phase} ({who})",
        importMd: "Import markdown",
        importMdTitle: "Import a meeting template from markdown",
        importMdHint: "Same idea as the framework editor: copy the blank template, edit in any editor, then paste or drop a .md file. Sections Before / During / After / AI summary become forms, note prompts, and summary direction. Bad markdown returns a clear error.",
        pickFile: "Choose .md file",
        pasteMd: "Paste your markdown here…",
        importCta: "Import & open",
        mdEmpty: "Paste or choose a markdown file first.",
        summaryTitle: "AI summary direction",
        summaryHint: "Markdown that steers the recap. Private notes are never sent to the model. Empty direction uses the current default.",
        summaryPlaceholder: "# Voice\nDirect, operational…",
        preview: "Preview",
      },
    },
    team: {
      title: "Co-founders & team",
      subtitle: "Invite teammates to collaborate on your company. Team members get shared access to metrics, roadmaps, frameworks, documents, and strategic discussions.",
      foundersOnly: "Only the principal founder or principal accelerator can manage a team.",
      acceleratorTitle: "Accelerator colleagues",
      acceleratorSubtitle: "Invite colleagues who share this accelerator. They get an email and are connected automatically with you and with the other colleagues. They can view and edit the company and coach portfolio.",
      acceleratorIntro: "Colleagues join the principal account. Community connections are sent automatically. Only the principal can import contacts from a CSV.",
      members: "Team members",
      primary: "Primary",
      cofounder: "Co-founder",
      cofounderIntro: "Mark teammates as co-founders. Co-founders are automatically added as required sign-offs on every new Living Doc — the primary founder is always a co-founder.",
      cofounderPrimaryHint: "The primary founder is always a co-founder.",
      markAsCofounder: "+ Mark as co-founder",
      removeCofounder: "Remove co-founder",
      pendingInvites: "Pending invites",
      expires: "Expires",
      invite: "Invite teammate",
      sendInvite: "Send invitation",
      alreadyMember: "This person is already on your team.",
      inviteSent: "Invitation sent.",
      lastSent: "Last sent: {when}",
      emailFailed: "The invitation is saved, but the email could not be sent. Use Resend.",
      resend: "Resend",
      resent: "Invitation sent again.",
      confirmCancel: "Cancel this pending invitation?",
      confirmRemove: "Remove this teammate? They will lose access to your company data.",
      optional: "optional",
      acceptTitle: "Join the team",
      acceptIntro: "{inviter} invited you to join {company} on TractionBoard.",
      acceptCta: "Accept invitation",
      acceptBusy: "Joining…",
      acceptSuccess: "You're in! Redirecting to your shared dashboard…",
      acceptNeedLogin: "You need to sign in (or create an account) with {email} to accept this invitation.",
      acceptInvalid: "This invitation link is invalid or has expired.",
      acceptWrongEmail: "This invitation was sent to {email}. Please sign in with that email.",
      invitedAs: "Invited as",
      iHaveAccount: "I already have an account",
      iAmNew: "I'm new — create my account",
      loginAndJoin: "Log in & join team",
      createAndJoin: "Create account & join team",
      wrongPassword: "Wrong password for this email. Try again or create an account.",
      noAccountYet: "No account yet?",
      haveAccount: "Already have an account?",
    },
    admin: {
      title: "Admin — user management",
      subtitle: "Edit roles, reset passwords, or remove user accounts. Visible only to administrators.",
      search: "Search",
      searchPlaceholder: "Search by name, email or company…",
      noResults: "No users match this search.",
      actions: "Actions",
      edit: "Edit",
      delete: "Delete",
      save: "Save changes",
      editTitle: "Edit user",
      resetPassword: "Reset password (optional)",
      resetPasswordHint: "Leave blank to keep current password",
      confirmDeleteTitle: "Delete this account?",
      confirmDeleteBody: "You're about to permanently delete",
      confirmDeleteHint: "This removes the user and all of their data (snapshots, metrics, roadmap, documents, connections, invites). This cannot be undone.",
      confirmDeleteCta: "Yes, delete permanently",
    },
    google: {
      title: "Calendar",
      hint: "You don't need a calendar connected. TractionBoard has its own. If you want, you can connect Google or Microsoft.",
      connectCta: "Connect Google",
      connected: "Google connected",
      disconnect: "Disconnect",
      confirmDisconnect: "Disconnect Google? Future sessions won't be added to that calendar.",
      errorGeneric: "Couldn't connect Google. Please try again.",
      notConfigured: "Google calendar is not configured on this server yet.",
      missingCalendarScope: "Calendar permission not granted. Click Disconnect then Connect again, and make sure to allow 'View and edit events' when Google asks.",
      optional: "You don't need a calendar connected. TractionBoard has its own. If you want, you can connect Google or Microsoft.",
    },
    microsoft: {
      connectCta: "Connect Microsoft calendar",
      connected: "Microsoft calendar connected",
      disconnect: "Disconnect",
      confirmDisconnect: "Disconnect Microsoft calendar? Future sessions won't be added to that calendar.",
      errorGeneric: "Couldn't connect Microsoft calendar. Please try again.",
      missingCalendarScope: "Calendar permission not granted. Click Disconnect then Connect again, and allow calendar access when Microsoft asks.",
      notConfigured: "Microsoft calendar is not configured on this server yet.",
    },
    mandates: {
      title: "Mandates",
      subtitle: "A mandate links a founder and an advisor. It sets the meeting length, frequency, start and end, and the cost if there is one. The advisor then has access to the founder's portfolio.",
      notReady: "Mandates are not ready yet. Book a meeting from Bookings for now.",
      emptyHidden: "Nothing here yet.",
      advisorEntrepreneurOnly: "Mandates are between advisors and entrepreneurs.",
      newMandate: "New mandate",
      counterparty: "Counterparty",
      pickCounterparty: "Pick a counterparty",
      noConnections: "You don't have any qualifying connections yet — connect first via",
      titleLabel: "Title",
      titlePlaceholder: "e.g., GTM strategy & 90-day execution",
      scope: "Scope / description",
      scopePlaceholder: "What is being delivered, by when, and how success is measured.",
      currency: "Currency",
      totalAmount: "Total amount",
      startDate: "Start date",
      endDate: "End date",
      duration: "Meeting length (minutes)",
      frequency: "Frequency",
      freq: { weekly: "Every week", biweekly: "Every two weeks", monthly: "Every month" },
      cost: "Cost charged to the founder",
      confirmRequest: "Ask both people to confirm",
      confirmHint: "Off by default. The Community connection and the advisor’s access to the portfolio are accepted automatically.",
      send: "Send mandate",
      bookFirst: "Book the first meeting",
      bookNext: "Book another meeting",
      createDraft: "Create draft",
      empty: "No mandates yet. Click \"New mandate\" to draft one.",
      youAreAdvisor: "You are the advisor",
      youAreFounder: "You are the founder",
      youAreAccelerator: "Created by your accelerator",
      advisor: "Advisor",
      entrepreneur: "Founder",
      milestones: "Milestones",
      noMilestones: "No milestones added yet.",
      addMilestone: "Add milestone",
      milestoneTitle: "Milestone title",
      milestoneDescription: "Description (what success looks like)…",
      editMilestones: "Edit milestones",
      edit: "Edit",
      save: "Save",
      summary: "Summary",
      actions: "Actions",
      propose: "Propose to counterparty",
      accept: "Accept",
      decline: "Decline",
      declineReason: "Reason (optional)",
      cancelMandate: "Cancel mandate",
      confirmCancel: "Cancel this mandate? Both sides will be notified.",
      markComplete: "Mark complete",
      due: "Due",
      deliveredOn: "Delivered",
      approvedOn: "Approved",
      viewDeliverable: "View deliverable",
      startMilestone: "Start",
      deliverMilestone: "Deliver",
      approveMilestone: "Approve",
      deliverableUrl: "Deliverable link (optional)",
      deliverableNote: "Note to founder (optional)",
      submitDelivery: "Submit delivery",
      status: {
        draft: "Draft",
        proposed: "Proposed",
        accepted: "Accepted",
        declined: "Declined",
        active: "Active",
        completed: "Completed",
        cancelled: "Cancelled",
      },
      milestoneStatus: {
        pending: "Pending",
        in_progress: "In progress",
        delivered: "Delivered",
        approved: "Approved",
        paid: "Paid",
      },
    },
    mit: {
      progress: "Progress",
      todo: "Not started",
      in_progress: "In progress",
      validated: "Validated",
      deliverable: "Deliverable",
      deliverablePlaceholder: "Link, file or summary of what was produced…",
      metric: "Success metric",
      gate: "Advancement gate",
      saveStep: "Save step",
      placeholder: "Your answer…",
    },
    documents: {
      title: "Documents",
      subtitleOwn: "Attach pitch decks, business plans, term sheets, financial models — any URL or file relevant to your company. Visible to your connected advisors / investors / accelerators.",
      subtitleViewer: "Documents shared by this founder. Click any item to open or download.",
      add: "Add document",
      modeUrl: "Link",
      modeFile: "Upload",
      titleField: "Title",
      titlePlaceholder: "Pitch deck v3, 2026 financial model…",
      urlField: "URL",
      urlRequired: "URL is required",
      fileField: "File",
      fileHint: "Max 8 MB. PDF, slides, docs, images.",
      fileRequired: "Please pick a file",
      fileEmpty: "That file is empty",
      titleRequired: "Title is required",
      downloadFailed: "Could not download this file",
      tooLarge: "File is larger than 8 MB",
      save: "Save",
      empty: "No documents yet.",
      confirmRemove: "Remove this document?",
    },
    portfolio: {
      title: "Portfolio",
      subtitleFounder: "Your company snapshot. Edit your Metrics, Roadmap and Framework, then \"Save & submit\" to share the latest version with your connections.",
      meetings: "Meetings",
      meetingsEmpty: "No meetings yet.",
      notes: "Notes",
      notesEmpty: "No notes yet.",
      notesHint: "You only see the notes you are allowed to see.",
      noteMeeting: "Meeting",
      meetingsUpcoming: "Upcoming",
      meetingsPast: "Past",
      subtitleFunnel: "Track the founders you work with through your own pipeline. Customize the stages, then pull founders in from your community connections.",
      subtitleAccelerator: "Two boards: companies you support, and coaches/advisors contributing.",
    },
    funnel: {
      editStages: "Edit stages",
      saveStages: "Save stages",
      addStage: "Add stage",
      removeStage: "Remove",
      empty: "Empty",
      addFounder: "Add founder",
      addAdvisor: "Add advisor",
      pickFounder: "Pick a founder",
      pickAdvisor: "Pick an advisor",
      noConnections: "Connect with a founder first via the Community tab.",
      allInFunnel: "Every company in your connections is already on the board.",
      unassignedHint: "Some people in your connections aren't in the funnel yet — add them, or visit",
      addNotes: "Add notes",
      editNotes: "Edit notes",
      notesPlaceholder: "Mandate scope, next step, last touchpoint…",
      confirmRemove: "Remove this founder from your funnel?",
      filterApply: "Apply",
      foundersBoard: "Companies",
      addCompany: "Add company",
      pickCompany: "Pick a company",
      advisorsBoard: "Coaches / Advisors",
      dataManagement: "Data management",
      cohort: "Cohort",
      year: "Year",
      archive: "Archive",
      downloadTemplate: "Download CSV template",
      downloadExport: "Download founder CSV",
      importTitle: "Import coaches and entrepreneurs",
      importPrincipalOnly: "Only the principal accelerator can import a CSV. You can still view and edit the portfolio.",
      exportTitle: "Export entrepreneur data",
      importPlaceholder: "Paste CSV or choose a file…",
      importSubmit: "Import CSV",
      importResult: "Imported {n}. Invalid rows: {e}. Last import: {file}, {when}",
      importPasted: "pasted CSV",
      dismissImport: "Dismiss",
      manualAdd: "Or type one person",
      manualName: "Full name",
      manualEmail: "Email",
      manualCompany: "Company",
      manualSubmit: "Add",
      exportFailed: "The export did not complete. Try again.",
      health: "Company health",
      healthSet: "Set health",
      healthGreen: "On track",
      healthYellow: "To watch",
      healthOrange: "Some issues",
      healthRed: "Major issue",
    },
    community: {
      title: "Community",
      subtitle: "Connect with founders, advisors, fund managers, service providers and accelerators.",
      mineTitle: "My connections",
      mineSubtitle: "People you have explicitly connected with. They can see your Metrics, Roadmap and Framework when you save & submit.",
      directoryTitle: "Discover",
      directorySubtitle: "Members who opted to appear in the public directory.",
      pendingIn: "Incoming requests",
      pendingOut: "Request sent",
      accept: "Accept",
      connect: "Connect",
      editProfile: "Edit",
      connected: "Connected",
      empty: "No one yet.",
      backTo: "Back to Community",
      lockedHint: "Connect with this founder to see their Metrics, Roadmap and Framework.",
      disconnect: "Disconnect",
      confirmDisconnect: "Disconnect from this person? You will both lose access to each other's shared data.",
      searchPlaceholder: "Search by name, company, or expertise…",
      allExpertises: "All expertise",
      filterAllMembers: "All members",
      filterAllFounders: "All founders",
      filterAllAdvisors: "All advisors",
      filterAllProviders: "All providers",
      filterAllInvestors: "All investors",
      filterAllAccelerators: "All accelerators",
      linkedin: "LinkedIn profile",
      meetings: "Meetings",
      meetingsSubtitle: "Meetings with this founder. Open a session to view notes, transcripts and summaries.",
      expertiseDetails: "Expertise in detail",
      inTheMedia: "In the media",
      languages: "Languages",
      contact: "Contact",
      links: "Links",
      recommend: "Recommend this advisor",
      recommended: "Recommended",
      recommendCount: "{n} recommendations",
      recommendCountOne: "1 recommendation",
      recommendCountZero: "No recommendations yet",
    },
    expertise: {
      strategy: "Strategy & business development",
      marketing: "Marketing & communications",
      sales: "Sales & commercial development",
      finance: "Finance & fundraising",
      legal: "Legal & governance",
      hr: "People & organization",
      tech: "Technology & innovation",
      operations: "Operations & logistics",
      impact: "Impact & sustainability",
    },
    roles: {
      founders: "Founders",
      founder: "Founder",
      advisors: "Advisors",
      advisor: "Advisor",
      providers: "Service providers",
      provider: "Service provider",
      fundManagers: "Fund managers",
      fundManager: "Fund manager",
      accelerators: "Accelerators",
      accelerator: "Accelerator",
      members: "Members",
      admin: "Admin",
    },
    snapshot: {
      metricsLabel: "Metrics — latest version",
      roadmapLabel: "Roadmap — latest version",
      frameworkLabel: "Framework — latest version",
      noneYet: "Not yet submitted",
      saveSubmit: "Save & submit",
      viewHistory: "View history",
      pickOne: "Pick a version to view.",
      historySubtitle: "Each \"Save & submit\" creates an immutable snapshot. The latest version is the one shared with your connections.",
      history_metrics: "Metrics — history",
      history_roadmap: "Roadmap — history",
      history_framework: "Framework — history",
    },
    terms: {
      title: "Session terms",
      when: "When",
      duration: "Duration",
      rate: "Rate",
      advisor: "Advisor",
      entrepreneur: "Founder",
      pending: "Awaiting confirmation",
      bothConfirmed: "Confirmed by both",
      confirm: "Confirm",
      proposeChanges: "Propose changes",
      propose: "Send proposal",
      cancelMeeting: "Cancel meeting",
      cancelConfirm: "Cancel this meeting? The other party will be notified, the calendar invite removed, and the auto-recording bot stopped.",
      deleteMeeting: "Delete",
      deleteConfirm: "Delete this meeting entirely? Since the other party hasn't confirmed yet, no record will be kept.",
      archive: "Archive",
      unarchive: "Unarchive",
      inviteSent: "Calendar invite sent",
      inviteNotSent: "Invite not sent yet",
      cancelSent: "Cancellation sent",
      resendInvite: "Resend invite",
      cancelPromptReason: "Reason (optional):",
      cancelled: "Cancelled",
    },
    detail: {
      back: "Back",
      joinMeeting: "Join meeting",
      addMeetingLink: "Add meeting link",
      updateMeetingLink: "Update link",
      pasteLinkPlaceholder: "https://meet.google.com/... · zoom.us/j/... · teams.microsoft.com/...",
      linkHelp: "Paste a Zoom, Teams, Webex, or Google Meet URL. If TractionBoard auto-generated a Meet link, it appears here — you can keep it or replace it.",
      save: "Save",
      preTitle: "Pre-meeting check-in",
      preForEntrepreneur: "Fill this in before the meeting. Your advisor can read it before the call.",
      preForAdvisor: "The founder's pre-meeting check-in.",
      preNotYet: "The entrepreneur hasn't submitted their check-in yet.",
      happiness: "Happiness level (1–10)",
      scaleRequired: "Choose a score from 1 to 10.",
      happinessReason: "Why this score?",
      challenges: "Top 5 challenges",
      opportunities: "Top 5 opportunities",
      priorities: "Top 5 priorities",
      priority30d: "#1 priority for the next 30 days",
      priority30dReason: "Why is this critical?",
      limitingFactor: "Main limiting factor right now",
      objective: "Additional details",
      blockerCategory: "What's blocking / slowing down?",
      blockerDetails: "Details",
      blockers: {
        resources: "Resources",
        sales: "Sales",
        product: "Product",
        team: "Team",
        clarity: "Strategic clarity",
        execution: "Execution",
        other: "Other",
      },
      submitPre: "Submit the check-in",
      preSubmitted: "Submitted",
      postTitle: "Post-meeting",
      postForAdvisor: "Fill this in after the meeting. TractionBoard then generates the summary.",
      postForOther: "Post-meeting notes from the other person.",
      postNotYet: "The post-meeting form hasn't been submitted yet.",
      recommendations: "Advisor recommendations (concrete actions, 7 days)",
      biggestRisk: "Biggest risk to watch",
      objective1: "Objective #1 (next 30 days)",
      metric: "Metric attached",
      owner: "Owner",
      watchpoint: "Watchpoint / signal that we're derailing",
      deadline: "Deadline",
      advisorNotes: "Additional notes",
      transcript: "Meeting transcript / verbatim (optional)",
      transcriptHelp: "Paste the meeting transcript here to improve AI summary quality.",
      fullTranscript: "Meeting transcript",
      refreshTranscript: "Refresh now",
      transcriptPending: "Transcript will appear here automatically a few minutes after the meeting ends.",
      transcriptStatus: {
        pending: "Pending",
        scheduled: "Bot scheduled",
        joining: "Bot joining",
        in_call: "Recording",
        recording_done: "Processing",
        available: "Available",
        failed: "Not attending",
        fatal: "Not attending",
        not_configured: "Recorder off",
        skipped_optout: "Recording off",
        no_meeting_url: "Waiting for link",
        done: "Done",
      },
      recall: {
        notAttendingTitle: "The TractionBoard recorder isn't scheduled to attend this meeting.",
        dispatchFailed: "The transcript bot couldn't be scheduled. This is usually a Recall.ai credit balance or API key issue on the platform side. Please contact your admin (or use Retry once the issue is resolved).",
        notConfigured: "The transcript bot is not configured on this environment. Contact the admin to enable Recall.ai.",
        skippedOptout: "Recording is opted out for this session. Turn opt-out off (and click Retry) to schedule the recorder.",
        noMeetingUrl: "No meeting link is set yet. Add a Meet/Zoom URL and the recorder will be scheduled.",
        showDetails: "Show technical details",
        retryScheduleBot: "Retry",
        retryFailed: "Retry didn't succeed — see details.",
      },
      hide: "Hide",
      show: "Show",
      submitPost: "Submit post-form & generate summary",
      summaryTitle: "AI-generated summary",
      summaryNotYet: "The summary will appear here after the post-meeting form is submitted.",
      regenerate: "Regenerate summary",
      addOne: "+ Add",
      removeOne: "Remove",
      mandatory: "Required",
    },
    notif: {
      title: "Notifications",
      none: "No notifications yet.",
      markAllRead: "Mark all read",
    },
    common: {
      loading: "Loading…",
      error: "Error",
      retry: "Retry",
      back: "Back",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving…",
      delete: "Delete",
      create: "Create",
      close: "Close",
    },
    framework: {
      title: "Frameworks",
      subtitle: "Standardized maturity frameworks (KTH Innovation Readiness Level, company lifecycle, …) to assess where a venture stands today and what comes next. Founders self-assess; advisors get a shared mental model to anchor conversations.",
      empty: "No framework yet — an admin can add one.",
      yourStage: "Your current stage",
      assignedTitle: "Assigned methodologies",
      chooseStageHint: "Choose your stage using the table below.",
      kthSelfTitle: "Self-assess: where are you on each readiness area?",
      kthSelfHelp: "Pick a level (1–9) for each area. Each readiness moves forward independently.",
    },
    metrics: {
      title: "Metrics",
      subtitle: "Track the 10 core metrics. Add or remove your own as your business evolves.",
      coreMetrics: "Core metrics",
      customMetrics: "Custom metrics",
      add: "Add metric",
      label: "Label",
      key: "Key (slug)",
      unit: "Unit",
      noCustom: "No custom metrics yet.",
      confirmDelete: "Delete metric",
      openGroups: "Metric Groups →",
    },
    metricGroups: {
      title: "Metric Groups",
      subtitle: "Build, share and track scorecards. Mix individual metrics into bundles tailored for any team or stakeholder.",
      newGroup: "New Metric Group",
      backToMetrics: "← Back to Metrics",
      backToList: "Back to list",
      edit: "Edit",
      assign: "Assign",
      assigned: "Assigned",
      unassign: "Unassign",
      track: "Track values →",
      viewValues: "View values",
      assignedBy: "Assigned by",
      targetUser: "Target",
      itemsCount: "{count} metric(s)",
      confirmDelete: "Delete this Metric Group? Assignments will also be removed.",
      emptyMine: "You haven't created any Metric Groups yet.",
      emptyAssigned: "No groups have been assigned to you yet.",
      emptyShared: "You haven't shared any groups with others yet.",
      tabs: { mine: "Mine", assigned: "Assigned to me", shared: "Shared by me" },
      assignTitle: "Assign group",
      assignSubtitle: "Pick one or more of your connections. They'll start tracking values for this group.",
      currentAssignees: "Currently assigned",
      yourConnections: "Your connections",
      noConnections: "You don't have any connections yet. Connect with founders or advisors first.",
      frequency: { daily: "Daily", weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" },
      types: { number: "Number", currency: "Currency", percent: "Percent", text: "Text" },
      builder: {
        createTitle: "Create Metric Group",
        editTitle: "Edit Metric Group",
        subtitle: "Name the group, then add the metrics it should track. Each metric has its own update frequency.",
        nameEn: "Name (EN)",
        nameFr: "Name (FR)",
        description: "Description",
        metrics: "Metrics in this group",
        addExisting: "Add existing metric",
        createNew: "Create new metric",
        noMetrics: "No metrics in this group yet. Add some to get started.",
        nameRequired: "Name (EN) is required.",
        atLeastOne: "Add at least one metric before saving.",
      },
      newDef: {
        title: "Create a new metric definition",
        key: "Key (slug, lowercase, no spaces)",
        labelEn: "Label (EN)",
        labelFr: "Label (FR)",
        unit: "Unit",
        type: "Type",
        description: "Description (optional)",
      },
      picker: {
        title: "Pick a metric to add",
        search: "Search by name, key or French label…",
        empty: "No matching metrics. Try creating a new one.",
      },
      track: {
        value: "Value",
        saved: "✓ Saved",
        history: "Recent",
      },
      view: {
        subtitle: "Latest values reported for this group.",
        lastUpdate: "Last update",
        noData: "No data yet — the assignee hasn't recorded any value.",
        history: "Recent",
      },
    },
    surveys: {
      title: "Surveys & Feedback",
      subtitle: "Help us shape TractionBoard by sharing your reality. Each takes about 5 minutes.",
      empty: "No surveys assigned yet.",
      questions: "questions",
      back: "Back to surveys",
      start: "Start",
      continue: "Continue",
      review: "Review",
      submit: "Submit",
      saveExit: "Save & exit",
      thanks: "Thank you!",
      thanksMsg: "Your input directly shapes what we build next.",
      backToApp: "Back to app",
      other: "Other (please specify)",
      upTo: "Pick up to {n}",
      allThatApply: "All that apply",
      rankHint: "Pick your top {n} (1 = most important)",
      promptTitle: "Help us improve TractionBoard",
      promptDuration: "Estimated time: {n} minutes",
      startNow: "Start survey",
      skip: "Skip for now",
      status: { not_started: "Not started", in_progress: "In progress", completed: "Completed", dismissed: "Dismissed" },
    },
    roadmap: {
      title: "Roadmap",
      subtitle: "Anchor everything to a north star, then cascade down to the next 2 weeks.",
      northStar: "North Star",
      northStarHelp: "The bold, irrational, lifetime ambition that decides everything.",
      year10: "10-year goal",
      year5: "5-year goal",
      year2: "2-year goal",
      year1: "1-year goal",
      quarters: "Next 4 quarters",
      month1: "Next 30 days",
      week2: "Next 2 weeks",
      save: "Save roadmap",
      saved: "Saved",
    },
    toolbox: {
      title: "Toolbox",
      subtitle: "Services, offerings, and solutions to plug into your advisory motion. More coming soon.",
      comingSoon: "Coming soon",
      live: "Available",
      frameworksTitle: "Frameworks",
      frameworksBody: "Browse, use, or build structured frameworks (sections, items, questions) to guide founders' diagnostics and execution.",
      servicesTitle: "Services",
      servicesBody: "On-demand expert services — finance, legal, growth, ops — bookable through your advisor.",
      offeringsTitle: "Offerings",
      offeringsBody: "Packaged programs and frameworks designed to accelerate specific outcomes.",
      solutionsTitle: "Solutions",
      solutionsBody: "Service-provider offerings — products, pricing, and how they help founders.",
      offersServicesTitle: "Offers and services",
      offersServicesBody: "Provider solutions, packaged programs, and on-demand expert services in one place.",
      agentsTitle: "Agents",
      agentsBody: "First-party copilots that sit on your meeting templates, notes, and summaries.",
    },
    agents: {
      meetingCopilot: "Meeting Copilot",
      meetingCopilotBody: "Prep, during-notes (share or private), and AI recaps directed by your markdown template.",
      useBefore: "Before: fill the shared prep form from your meeting template.",
      useDuring: "During: capture notes tagged shared or private.",
      useAfter: "After: wrap-up form for decisions and owners.",
      useSummary: "Summary: generate a recap using your AI-direction markdown (never private notes).",
      yourTemplates: "Your meeting templates",
      hasSummaryDir: "has AI direction",
    },
    solutions: {
      title: "Solutions",
      subtitle: "Browse service-provider offerings. Each page is a one-pager the provider can edit.",
      empty: "No published solutions yet.",
      backTo: "All solutions",
      edit: "Edit this page",
      editMine: "Edit my solution page",
      editTitle: "Solution page",
      editHint: "This is the offering founders see: company, two (or more) products, pricing, and why they should choose you.",
      viewLive: "View page",
      viewOffering: "View solution",
      providerKicker: "Service provider",
      contact: "Contact",
      website: "Website",
      company: "Company",
      tagline: "Tagline",
      headline: "Headline",
      contactEmail: "Contact email",
      problem: "The risk",
      howWeHelp: "How we help",
      products: "Products",
      productN: "Product {n}",
      productName: "Product name",
      productDescription: "Description",
      covers: "Built for",
      coversHint: "Who / what this product covers (one per line)",
      bulletsHint: "Capabilities (one per line)",
      onePerLine: "One item per line",
      pricingSummary: "Pricing — short",
      pricingDetails: "Pricing — details",
      addProduct: "Add a product",
      whyChoose: "Why clients choose us",
      reasonN: "Reason {n}",
      reasonTitle: "Title",
      reasonBody: "Detail",
      addReason: "Add a reason",
      remove: "Remove",
      published: "Publish in the Solutions directory",
      save: "Save solution page",
      providersOnly: "Only service providers can edit a solution page.",
      stats: "Key figures",
      statN: "Figure {n}",
      statValue: "Number",
      statLabel: "What it means",
      addStat: "Add a figure",
      workflow: "How it works",
      workflowHint: "Workflow steps (one per line)",
      compliance: "Contributes to",
      complianceHint: "Compliance frameworks (one per line)",
      proof: "In production",
      proofN: "Proof {n}",
      addProof: "Add a proof point",
      coversLabel: "List heading (e.g. To protect / Threats assessed)",
    },
    fwList: {
      title: "Frameworks",
      subtitle: "A reusable diagnostic, organized into sections, items, and questions, with progress you can follow. Advisors, accelerators, fund managers, and service providers can build their own.",
      create: "Build a framework",
      import: "Import a framework",
      mine: "My frameworks",
      mineEmpty: "You haven't created any frameworks yet.",
      shared: "Shared with you",
      sharedEmpty: "No frameworks have been shared with you yet.",
      library: "Library",
      system: "System",
      by: "By",
      edit: "Edit",
    },
    fwImport: {
      title: "Import a framework",
      subtitle: "Paste a framework written in our template. We'll parse it and auto-translate to the other language.",
      step1Title: "Step 1 — Get the template",
      step1Hint: "Copy or download this template, then paste it into ChatGPT / Claude / Gemini and ask the LLM to fill it for your topic.",
      copy: "Copy template",
      download: "Download .md",
      step2Title: "Step 2 — Paste the filled template",
      step2Hint: "Paste the completed template here. We'll parse the sections, items, questions and references. The other language is auto-translated on import.",
      pastePlaceholder: "# My framework\n\nDescription: …\n\n## Section 1\n\n### Item 1\nQuestion: …\n",
      importCta: "Import & open",
      importing: "Importing…",
      empty: "Paste a filled template before importing.",
    },
    fwBuilder: {
      title: "Framework builder",
      titleNew: "Build a framework",
      titleEdit: "Edit framework",
      editingIn: "Editing in",
      langHint: "Edits apply to the language shown above. Switch the global EN/FR toggle to edit the other language. The other language is auto-filled on first save.",
      roleNotAllowed: "Only advisors, accelerators, fund managers, service providers and admins can build frameworks.",
      notFound: "Framework not found or not visible to you.",
      basics: "Basics",
      titleLabel: "Title",
      titleRequired: "Title is required.",
      slug: "URL slug",
      slugRequired: "Slug is required.",
      descLabel: "Description",
      visibility: "Visibility",
      visPrivate: "Private (only me & admins)",
      visConnections: "Shared with my connections",
      visPublic: "Public (everyone on TractionBoard)",
      sections: "Sections",
      addSection: "Add section",
      noSections: "No sections yet. Add one to get started.",
      sectionName: "Section name",
      addItem: "Add item",
      itemName: "Item name",
      questions: "Questions",
      addQuestion: "Add question",
      questionLabel: "Question",
      references: "References",
      referencesHint: "Links to source material, books, or articles supporting this framework. Shown at the end of the framework.",
      addReference: "Add reference",
      refLabel: "Label (optional)",
      legalNotice: "Legal notice",
      legalLabel: "Legal notice",
      legalHint: "Disclaimer or copyright notice. Shown at the very end of the framework when used.",
      save: "Save framework",
      deleteFramework: "Delete framework",
      confirmDeleteFramework: "Delete this framework? All founder answers tied to it will also be deleted.",
      confirmDeleteSection: "Delete this section and all its items?",
      confirmDeleteItem: "Delete this item and its questions?",
    },
    journal: {
      title: "Journal",
      subtitle: "Your team's shared thinking. Capture wins, decisions, blockers — the Brain organizes it.",
      inputPlaceholder: "What's on your mind? (Cmd+Enter to post)",
      post: "Post",
      posting: "Posting…",
      empty: "Nothing here yet. Type your first thought above.",
      emptyForRole: "The Journal is your team's daily workspace. Only founders and their team members can journal.",
      loadMore: "Load more",
      loading: "Loading…",
      visibility: {
        private: "Private",
        team: "Team",
        team_advisor: "Team + Advisor",
        team_advisor_investor: "Team + Advisor + Investor",
      },
      visibilityHint: {
        private: "Only you see this",
        team: "All cofounders see this",
        team_advisor: "Cofounders + assigned advisor",
        team_advisor_investor: "Cofounders + advisor + investors",
      },
      source: {
        native: "written here",
        telegram: "via Telegram",
        imported_llm: "imported from AI chat",
        brain: "from the Brain",
        system: "system",
      },
      actions: {
        edit: "Edit",
        delete: "Delete",
        save: "Save",
        cancel: "Cancel",
        editing: "Editing…",
        confirmDelete: "Delete this entry? This cannot be undone.",
      },
      badges: {
        edited: "edited",
        extracting: "Brain is reading…",
        proposalsReady: "{count} suggestion(s)",
      },
      relative: {
        justNow: "just now",
        minutesAgo: "{n}m ago",
        hoursAgo: "{n}h ago",
        yesterday: "yesterday",
        daysAgo: "{n}d ago",
      },
      errors: {
        tooShort: "Type something first",
        tooLong: "Too long (max 20000 characters)",
        generic: "Something went wrong. Try again.",
      },
      askBrain: "Ask the Brain",
      viewProposals: "Review suggestions",
      importShareLink: "Import AI chat",
      importPrompt: "Paste a ChatGPT / Claude / Perplexity share URL",
      importNotePrompt: "Optional note about this conversation",
    },
    brain: {
      title: "Brain",
      subtitle: "Ask anything about your company. The Brain reads your journal, metrics, roadmap, and documents.",
      you: "You",
      brain: "Brain",
      ask: "Ask",
      thinking: "Thinking…",
      inputPlaceholder: "Ask about metrics, decisions, customers, roadmap… (Cmd+Enter)",
      newConversation: "New conversation",
      noConversations: "No conversations yet.",
      untitled: "Untitled",
      emptyForRole: "The Brain is available to entrepreneurs and their team.",
      starterHint: "Try asking:",
      starters: {
        risks: "What are our top 3 risks right now?",
        recent: "What has changed in the last 2 weeks?",
        metrics: "What are our current metrics?",
      },
      errors: {
        generic: "Something went wrong. Try again.",
      },
    },
    proposals: {
      title: "Suggestions",
      subtitle: "The Brain proposes updates from your journal entries. Approve what's accurate; skip the rest.",
      loading: "Loading…",
      empty: "No pending suggestions. Keep journaling and the Brain will surface signals.",
      emptyForRole: "Suggestions are available to entrepreneurs and their team.",
      apply: "Apply",
      skip: "Skip",
      kinds: {
        metric_delta: "Metric",
        decision: "Decision",
        roadmap_change: "Roadmap",
        kpi_note: "KPI note",
      },
      errors: { generic: "Something went wrong. Try again." },
    },
    memory: {
      title: "Memory",
      subtitle: "Everything the Brain sees about your company — with automation levels, evidence, and audit trail.",
      loading: "Loading memory…",
      lastUpdated: "Last updated",
      sections: {
        journal: "Journal",
        metrics: "Metrics",
        roadmap: "Roadmap",
        documents: "Documents",
        sessions: "Advisory sessions",
        mandates: "Mandates",
        framework: "Framework state",
        proposals: "Extraction proposals",
        brain: "Brain conversations",
        decisions: "Decisions",
      },
      counts: {
        entries: "entries",
        items: "items",
        pending: "pending",
        applied: "applied",
        skipped: "skipped",
      },
      viewAll: "View all",
      empty: "Nothing here yet.",
      auditTitle: "Audit trail",
      auditEmpty: "No changes recorded yet.",
      auditUndo: "Undo",
      auditUndone: "Undone",
      auditConfirmUndo: "Undo this change? It will restore the previous state.",
      auditWindowExpired: "Undo window (24h) expired.",
      levels: {
        0: "Manual",
        1: "AI Suggested",
        2: "Imported",
        3: "Automated",
        4: "Computed",
      },
      levelBadge: "L{n}",
      openLink: "Open",
      brainScore: "Company knowledge",
      trustNote: "Every fact carries an owner, source, and evidence.",
    },
    memoryApply: {
      title: "Apply suggestion",
      subtitle: "Review before writing to your company brain.",
      targetLabel: "Where should this go?",
      newValue: "New value",
      unit: "Unit",
      metricName: "Metric name",
      noteText: "Note",
      tier: "Roadmap tier",
      tiers: {
        north_star: "North star",
        year_10: "10 years",
        year_5: "5 years",
        year_2: "2 years",
        year_1: "1 year",
        quarter_0: "Quarter 1",
        quarter_1: "Quarter 2",
        quarter_2: "Quarter 3",
        quarter_3: "Quarter 4",
        month_1: "Next month",
        week_2: "Next 2 weeks",
      },
      newText: "Text",
      mode: "Mode",
      modeAppend: "Add to existing",
      modeReplace: "Replace existing",
      decisionTitle: "Decision title",
      decisionOwner: "Owner",
      decisionContext: "Context",
      cancel: "Cancel",
      confirm: "Apply and write",
      applying: "Applying…",
      applied: "Applied — new value saved.",
    },
    integrations: {
      title: "Integrations",
      subtitle: "Every source of truth feeding your company brain. Trusted knowledge starts here.",
      loading: "Loading…",
      status: {
        connected: "Connected",
        coming_soon: "Coming soon",
        disconnected: "Disconnected",
      },
      type: {
        manual: "Manual",
        api: "API",
        file: "File",
        ai_extraction: "AI extraction",
      },
      mode: {
        read_only: "Read-only",
        read_write: "Read / Write",
      },
      empty: "No integrations available.",
      groups: {
        connected: "Connected today",
        comingSoon: "Coming soon",
      },
    },
    onboarding: {
      title: "AI Onboarding",
      subtitle: "A conversational partner that builds your Company Brain, one question at a time.",
      foundersOnly: "This experience is available to founders.",
      welcomeTitle: "Meet your Company Brain",
      welcomeBody: "Instead of filling forms, I'll interview you like a business partner and progressively build a living memory of your company — strategy, customers, metrics, and priorities. Takes about 10 minutes; you can pause anytime.",
      start: "Start conversation",
      starting: "Starting…",
      you: "You",
      brain: "Brain",
      send: "Send",
      sending: "Sending…",
      placeholder: "Type your answer… (Enter to send, Shift+Enter for newline)",
      dismiss: "Dismiss",
      finish: "I'm done for now",
      brainScore: "Company knowledge",
      brainScoreHint: "Builds as you share more with your Brain.",
      confirmDismiss: "Dismiss the onboarding? You can come back to it from the banner on your home page.",
      confirmComplete: "Mark the initial Company Brain as ready?",
      error: "Something went wrong. Try again.",
      phases: {
        foundation: "Foundation",
        reality: "Current reality",
        direction: "Direction",
        measurement: "Measurement",
        assets: "Assets",
      },
      banner: {
        startTitle: "Start your Company Brain",
        startSub: "Let TractionBoard interview you for 10 minutes to build your living company memory.",
        progressTitle: "Continue building your Brain · {pct}%",
        progressSub: "Every answer teaches the AI more about your company.",
        resumeTitle: "Resume your onboarding",
        start: "Start",
        continue: "Continue",
      },
    },
    docs: {
      title: "Docs",
      subtitle: "Living plans, playbooks, and strategy documents — with versioning, sign-off, and Brain chat per doc.",
      loading: "Loading docs…",
      empty: "No docs yet. Create your first one.",
      emptyBig: "No living docs yet.",
      loadStarterPack: "Load starter memory pack",
      seedPack: "Add starter pack",
      seedPackHint: "Creates 6 pre-filled Living Docs (COMPANY, STRATEGY, PEOPLE, CUSTOMERS, PLAYBOOK, INTEGRATIONS). Skips any that already exist.",
      errorSeed: "Could not seed starter pack.",
      new: "New doc",
      newTitle: "Create a new doc",
      type: "Type",
      docTitle: "Title",
      cancel: "Cancel",
      create: "Create",
      creating: "Creating…",
      selectHint: "Select a document from the sidebar, or create a new one.",
      save: "Save",
      saving: "Saving…",
      archive: "Archive",
      archiveConfirm: "Archive this document? You can still view it from the audit trail.",
      publish: "Publish as official",
      versionNotePlaceholder: "Version note (optional) — what changed?",
      versionHistory: "Version history",
      restore: "Restore",
      restoreConfirm: "Load this version into the editor? Save to keep it.",
      chatBtn: "Ask this doc",
      chatTitle: "Ask questions about this document",
      chatPlaceholder: "e.g. What's the North Star for Q1?",
      ask: "Ask",
      signoffBtn: "Sign-offs",
      signoffTitle: "Sign-offs",
      signoffIntro: "Choose cofounders who must sign off before this doc can be marked official.",
      requestSignoff: "Request sign-offs",
      signedBy: "Signed by",
      signCommentPh: "Optional comment…",
      signNow: "Sign off now",
      signedCount: "{signed}/{total} signed",
      statusDraft: "Draft",
      statusOfficial: "Official",
      errorCreate: "Could not create doc.",
      errorSave: "Could not save.",
      errorPublish: "Cannot publish yet.",
      errorSignoff: "Sign-off failed.",
      errorChat: "Chat failed.",
      types: {
        "90_day": "90-day plan",
        "1_year": "1-year plan",
        gtm: "GTM plan",
        playbook: "Playbook",
        strategy: "Strategy",
        prd: "PRD",
        custom: "Custom",
      },
      cofounder: "Co-founder",
      delete: "Delete",
      deleteConfirm: "Permanently delete this doc? This can't be undone.",
      tip: {
        version: "Version history — click to view or restore an older version.",
        askDoc: "Ask an AI question grounded in this doc's content.",
        signoffs: "Manage who must sign off before this doc becomes official.",
        archive: "Hide this doc from the sidebar (still recoverable).",
        delete: "Permanently delete this doc.",
        publish: "Publish as official once every required sign-off is in.",
        save: "Save your changes and bump the version.",
        help: "How Living Docs work",
      },
      help: {
        title: "How Living Docs work",
        pillsTitle: "Statuses & actions",
        catsTitle: "Doc categories",
        vettingIntro: "Co-founders (set on the Team page) are automatically added as required sign-offs on every new doc. You can add or remove reviewers per doc from the Sign-offs panel.",
        pill: {
          draft: "The doc is a work-in-progress and can be edited freely.",
          official: "Every required sign-off is in; the doc represents the current source of truth.",
          pending: "The doc is waiting for one or more sign-offs before it can be published as official.",
          version: "Each save bumps the version. Full history is preserved and restorable.",
          askDoc: "Ask an AI question — the answer is grounded in this doc's content.",
          signoffs: "Pick the people who must approve before this doc becomes official.",
          archive: "Hides the doc from the sidebar. You can restore it from the audit trail.",
          delete: "Permanently removes the doc. Owner and admin only.",
          publish: "Marks the doc as official. Blocked until every sign-off is done.",
          save: "Saves your edits and creates a new version.",
        },
        cat: {
          "90_day": "Quarterly plan: what your team commits to shipping over the next 90 days.",
          "1_year": "Annual plan: high-level milestones, revenue, hires, product bets for the year.",
          gtm: "Go-to-market playbook: ICP, positioning, channels, pricing, funnel.",
          playbook: "Reusable operational recipes: onboarding, sales, customer support, incidents.",
          strategy: "Long-term strategy: mission, moat, positioning, category, distribution bets.",
          prd: "Product Requirements Document for a specific feature or product line.",
          custom: "Free-form document — meeting notes, memos, or anything not covered above.",
        },
      },
    },
    legal: {
      centerTitle: "Legal Center",
      centerSubtitle: "All agreements and policies that govern TractionBoard. Versions and last-updated dates below.",
      loading: "Loading…",
      required: "Required at signup",
      versionLabel: "Version",
      effectiveDate: "Effective date",
      updated: "Last updated",
      jurisdiction: "Jurisdiction",
      backToCenter: "Back to Legal Center",
    },

  },
  fr: {
    appName: "TractionBoard",
    nav: {
      dashboard: "Réservations",
      journal: "Journal",
      brain: "Brain",
      sessions: "Rencontres",
      newSession: "Planifier",
      clients: "Clients",
      community: "Communauté",
      portfolio: "Portfolio",
      framework: "Méthodologies",
      metrics: "Métriques",
      roadmap: "Feuille de route",
      toolbox: "Boîte à outils",
      mandates: "Mandats",
      logout: "Déconnexion",
    },
    menu: {
      profile: "Profil",
      sendFeedback: "Envoyer un commentaire",
      surveys: "Sondages",
      whatsNext: "À venir",
      changePassword: "Changer le mot de passe",
      language: "Langue",
      signOut: "Se déconnecter",
      adminConsole: "Console admin",
    },
    adminConsole: {
      title: "Admin · TractionBoard",
      subtitle: "Tableau de bord interne",
      refresh: "Actualiser",
      byRoleTitle: "Utilisateurs par rôle",
      tabs: { overview: "Vue d'ensemble", feedback: "Feedback", users: "Utilisateurs", roadmap: "Roadmap", settings: "Paramètres", waitlist: "Liste d'attente", flows: "Flow" },
      kpi: {
        totalUsers: "Utilisateurs total",
        wau7: "WAU (7j)",
        totalSessions: "Rencontres",
        activeMandates: "Mandats actifs",
        connections: "Connexions",
        newFeedback: "Feedback nouveau",
        scheduled: "prévues",
        completed: "terminées",
        total: "total",
        allTime: "depuis le début",
      },
      feedback: {
        allStatuses: "Tous les statuts",
        bug: "Bug",
        feature: "Suggestion",
        note: "Note",
        empty: "Aucun feedback ne correspond à ces filtres.",
        status: {
          new: "nouveau",
          reviewed: "examiné",
          planned: "planifié",
          fixed: "corrigé",
          ignored: "ignoré",
        },
        promote: "Promouvoir",
        promoteHint: "Ajouter ce feedback comme nouvel item sur la roadmap publique",
        promoteConfirm: "Promouvoir ce feedback vers la roadmap publique ?",
        promoteDone: "Ajouté à la roadmap en 'À l'étude'. Vous pouvez le glisser plus loin dans l'onglet Roadmap.",
      },
      settings: {
        loading: "Chargement…",
        confirmDisconnect: "Déconnecter le calendrier système TractionBoard ? Les nouvelles réservations devront revenir aux liens de réunion manuels jusqu'à la reconnexion.",
        systemCalendar: {
          title: "Calendrier système TractionBoard",
          description: "Connectez un compte Google Workspace appartenant à TractionBoard (ex. calendar@tractionboardai.com). Lorsque le créateur d'une réservation n'a pas connecté son propre compte Google, ce compte système génère automatiquement un lien Google Meet — chaque session a ainsi un lien vidéo et une transcription Recall.ai.",
          connected: "Connecté",
          notConnected: "Non connecté",
          since: "Connecté depuis",
          connect: "Connecter le compte Google",
          connecting: "Redirection vers Google…",
          disconnect: "Déconnecter",
          howItWorks: "Comment ça fonctionne",
          step1: "Si l'advisor/créateur a connecté Google → l'événement vit sur son calendrier (comportement existant).",
          step2: "Sinon → TractionBoard crée l'événement sur ce compte système et un lien Meet est généré automatiquement.",
          step3: "L'invitation ICS est toujours envoyée par TractionBoard en parallèle — les utilisateurs sans Google reçoivent aussi l'événement.",
        },
      },
    },
    adminWaitlist: {
      modeTitle: "Mode d'inscription",
      modeHelp: "Le mode Liste d'attente capture les nouvelles inscriptions comme des demandes d'accès à approuver. Le mode Ouvert permet à quiconque de s'inscrire directement.",
      modeWaitlist: "Sur invitation (liste d'attente)",
      modeOpen: "Ouvert (inscription publique)",
      mode_waitlist: "Sur invitation",
      mode_open: "Ouvert",
      modeCurrent: "Mode actuel : {mode}",
      status_pending: "En attente",
      status_approved: "Approuvées",
      status_rejected: "Rejetées",
      empty: "Aucune demande ici.",
      approve: "Approuver",
      reject: "Rejeter",
      confirmApprove: "Approuver cette demande ? Un courriel d'invitation sera envoyé pour définir un mot de passe.",
      confirmReject: "Rejeter cette demande d'accès ?",
      col: { name: "Nom", email: "Courriel", role: "Rôle", company: "Entreprise", note: "Note", created: "Demandée" },
    },
    adminFlows: {
      intro: "Parcours génériques (pas une cohorte nommée). Chaque étape indique la vue (fondateur, accélérateur, conseiller, admin, public). Comptes lab : founder@test.tb / Test12345! — même mot de passe pour cofounder, accelerator, coach. Les notes restent dans ce navigateur.",
      signup: "S’inscrire",
      adminApprove: "Autoriser en admin (wait-list)",
      emailLink: "Cliquer le lien du courriel pour définir le mot de passe",
      setPassword: "Définir son nouveau mot de passe",
      loginForgot: "Connexion / mot de passe oublié",
      arrival: "Arrivée sur TractionBoard",
      recs: "Recommandations (Must / Nice to have)",
      stepNotes: "Écarts, bugs, formulation…",
      screenshotTbd: "Capture à venir",
      emailStub: "Local : RESEND_API_KEY vide → EMAIL stub dans les logs, pas de boîte mail.",
      workingNotes: "Notes de travail / bugs",
    },
    whatsNext: {
      publicTitle: "À venir sur TractionBoard",
      publicIntro: "Voici ce sur quoi on travaille maintenant, ce qui arrive bientôt et ce qu'on a livré récemment. Construit en grande partie grâce à vos commentaires.",
      publicEmpty: "Aucun item public pour le moment. Revenez bientôt !",
      publicCta: "Une idée ou un bug à signaler ?",
      sendFeedback: "Envoyez-nous un commentaire",
      col: {
        considering: "À l'étude",
        up_next: "Prochainement",
        in_progress: "En cours",
        shipped: "Livré",
      },
      adminHint: "Glissez les cartes entre les colonnes pour mettre à jour le statut. « À l'étude » est masqué au public.",
      addItem: "Ajouter un item",
      titlePlaceholder: "Qu'est-ce qu'on construit ?",
      descPlaceholder: "Détails optionnels (visibles aux utilisateurs une fois publié)…",
      emptyCol: "Vide",
      linkedFeedback: "liés",
      confirmDelete: "Supprimer cet item de la roadmap définitivement ?",
    },
    comingSoon: {
      backToToolbox: "Retour à la boîte à outils",
    },
    feedback: {
      title: "Envoyer un commentaire",
      intro: "TractionBoard est en beta — votre avis façonne le produit. Dites-nous ce qui fonctionne, ce qui casse, ce que vous aimeriez.",
      ratingLabel: "Comment se passe votre expérience ?",
      tapStar: "Touchez une étoile",
      rating1: "Frustrant",
      rating2: "Peut mieux faire",
      rating3: "C'est correct",
      rating4: "Plutôt bien",
      rating5: "J'adore",
      bugLabel: "Un bug ?",
      bugPlaceholder: "Décrivez ce qui ne va pas…",
      featureLabel: "Suggestion de fonctionnalité ?",
      featurePlaceholder: "Qu'est-ce qui améliorerait TractionBoard pour vous ?",
      anythingLabel: "Autre chose ?",
      anythingPlaceholder: "Vos pensées, encouragements, ou n'importe quoi…",
      submit: "Envoyer",
      cancel: "Annuler",
      imageLabel: "Joindre des captures",
      imageHelp: "Optionnel. Jusqu'à deux images, 3 Mo chacune.",
      attachImage: "Joindre des images",
      removeImage: "Retirer",
      imageTooBig: "Image trop grande (max 3 Mo)",
      tooManyImages: "Vous pouvez joindre deux images.",
      imageOnly: "Choisissez un fichier image.",
      videoHelp: "Optionnel. Un enregistrement d'écran, 8 Mo maximum.",
      attachVideo: "Joindre un enregistrement d'écran",
      videoOnly: "Choisissez un fichier vidéo.",
      videoTooBig: "Enregistrement trop lourd (max 8 Mo)",
      footnote: "Lié à votre compte pour qu'on puisse faire un suivi si besoin.",
      thanks: "Merci !",
      thanksBody: "Votre commentaire est arrivé dans notre boîte de réception.",
    },
    auth: {
      loginTitle: "Connexion",
      registerTitle: "Créer votre compte",
      email: "Courriel",
      password: "Mot de passe",
      name: "Nom complet",
      company: "Entreprise",
      role: "Je suis…",
      roleEntrepreneur: "Fondateur / Entrepreneur",
      roleAdvisor: "Conseiller",
      roleProvider: "Fournisseur de service",
      roleInvestor: "Gestionnaire de fonds",
      roleAccelerator: "Accélérateur",
      lang: "Langue",
      langEn: "English",
      langFr: "Français",
      submitLogin: "Connexion",
      submitRegister: "Créer le compte",
      submitRequestAccess: "Demander l'accès",
      switchToRegister: "Pas de compte ? S'inscrire",
      switchToLogin: "Déjà un compte ? Se connecter",
      inviteOnlyNote: "TractionBoard est actuellement sur invitation. Remplissez le formulaire ci-dessous et un administrateur examinera votre demande.",
      noteLabel: "Pourquoi souhaitez-vous nous rejoindre ? (optionnel)",
      notePlaceholder: "Dites-nous en un peu plus sur vous, votre entreprise, ou ce qui vous amène sur TractionBoard.",
      waitlistTitle: "Vous êtes sur la liste d'attente",
      waitlistBody: "Merci — nous avons bien reçu votre demande depuis {email}. TractionBoard est actuellement sur invitation. Un administrateur examinera votre demande et vous enverra un courriel une fois votre compte approuvé.",
      waitlistAlready: "Vous aviez déjà soumis une demande — nous avons conservé la précédente.",
      consentRequired: "Veuillez accepter les trois ententes pour continuer.",
      consentTermsPrefix: "J'ai lu et j'accepte les",
      consentTermsLink: "Conditions d'utilisation",
      consentTermsSuffix: ".",
      consentAnd: "et la",
      consentPrivacyPrefix: "J'ai lu et j'accepte la",
      consentPrivacyLink: "Politique de confidentialité",
      consentPrivacySuffix: ".",
      consentBetaPrefix: "Je comprends que TractionBoard est en bêta privée et j'accepte les",
      consentBetaLink: "Conditions du programme bêta",
      consentBetaSuffix: ".",
      loading: "Un instant…",
      forgotLink: "Mot de passe oublié ?",
      forgotTitle: "Réinitialiser votre mot de passe",
      forgotHelp: "Entrez votre courriel et nous vous enverrons un lien de réinitialisation.",
      sendResetLink: "Envoyer le lien",
      checkEmailTitle: "Vérifiez vos courriels",
      checkEmailBody: "Si un compte existe pour ce courriel, un lien de réinitialisation est en route. Il expire dans 1 heure.",
      backToLogin: "Retour à la connexion",
      resetTitle: "Définir un nouveau mot de passe",
      newPassword: "Nouveau mot de passe",
      confirmPassword: "Confirmer le mot de passe",
      setNewPassword: "Mettre à jour",
      passwordsDontMatch: "Les mots de passe ne correspondent pas.",
      passwordTooShort: "Le mot de passe doit faire au moins 6 caractères.",
      resetDoneTitle: "Mot de passe mis à jour",
      resetDoneBody: "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
      goToLogin: "Se connecter",
    },
    dash: {
      greeting: "Bonjour",
      upcoming: "Rencontres à venir",
      past: "Rencontres passées",
      noUpcoming: "Aucune rencontre à venir.",
      noPast: "Aucune rencontre passée.",
      book: "Planifier une rencontre",
      bookMandate: "Réserver un mandat",
      noMandates: "Aucun mandat pour le moment.",
      activeMandates: "Mandats actifs",
      pastMandates: "Mandats passés",
      noActiveMandates: "Aucun mandat actif.",
      noPastMandates: "Aucun mandat passé.",
      advisorCTA: "Consultez votre agenda & prép",
      observer: "Vous verrez les rencontres où vous êtes observateur.",
      viewAll: "Voir tout",
      createMandate: "Créer un mandat",
      acceleratorHint: "Les rencontres des fondateurs connectés apparaissent ici. Notes partagées, transcriptions et résumés IA sont en lecture seule.",
      filterAllFounders: "Tous les fondateurs",
      filterAllAdvisors: "Tous les conseillers",
      filterAllPrograms: "Tous les programmes",
      filterAllCohorts: "Toutes les cohortes",
      filterAllYears: "Toutes les années",
    },
    team1v1: {
      bookButton: "Réserver un collègue",
      bookTitle: "Planifier une rencontre avec un collègue",
      with: "Avec",
      pickTeammate: "Choisir un collègue",
      noTeammates: "Invitez d'abord un collègue — ces rencontres sont réservées à votre équipe.",
      template: "Modèle",
      pickTemplate: "Choisir un modèle",
      manageTemplates: "Gérer les modèles",
      notes: "Notes (optionnel)",
      schedule: "Planifier la rencontre",
      agenda: "Avant la rencontre",
      myAgenda: "Ma préparation",
      theirAgenda: "Sa préparation",
      submitAgenda: "Soumettre la préparation",
      agendaSubmitted: "Soumis le {date}",
      waitingForThem: "En attente de leur préparation.",
      emptyState: "Planifiez votre première rencontre avec un collègue",
      summary: "Résumé IA",
      generateSummary: "Générer le résumé",
      summaryNotYet: "Le résumé apparaît après les deux préparations et la transcription — ou générez-le maintenant.",
      topics: "Sujets clés",
      decisions: "Décisions et engagements",
      followUps: "Suivis pour la prochaine rencontre",
      sentiment: "Signaux de sentiment",
      detailTitle: "Rencontre avec {name}",
      badge: { advisory: "Rencontre", internal_1_1: "Collègue", mandate: "Mandat" },
      withPerson: "avec {name}",
      duringNotes: "Notes pendant la rencontre",
      duringNotesHint: "Choisissez qui voit chaque note : tout le monde dans la rencontre, l'accélérateur, ou vous seul. Les notes privées n'entrent jamais dans le résumé IA.",
      noNotes: "Pas encore de notes.",
      shared: "Partagée avec tous",
      sharedAccelerator: "Partagée avec l'accélérateur",
      private: "Privée",
      makePrivate: "Rendre privée",
      makeShared: "Partager avec tous",
      makeSharedAccelerator: "Partager avec l'accélérateur",
      notePlaceholder: "Capturer ce qui se dit ou se décide…",
      addNote: "Ajouter une note",
      afterForm: "Après la rencontre",
      myAfter: "Mon bilan",
      theirAfter: "Son bilan",
      submitAfter: "Soumettre le bilan",
      waitingAfter: "En attente de leur bilan.",
    },
    sessions: {
      title: "Rencontres",
      scheduled: "Planifiée",
      completed: "Complétée",
      withAdvisor: "avec",
      open: "Ouvrir",
      status: "Statut",
      preDone: "Pré-formulaire rempli",
      preMissing: "Pré-formulaire en attente",
      postDone: "Post-formulaire rempli",
      summaryReady: "Résumé prêt",
      recapPending: "Compte rendu en attente",
      filterAll: "Toutes",
      filterUpcoming: "À venir",
      filterPast: "Passées",
      viewArchive: "Archives",
      backToActive: "Retour aux actives",
      archiveEmpty: "Aucune rencontre archivée pour le moment.",
    },
    book: {
      title: "Planifier une rencontre",
      with: "Avec",
      pickPerson: "Choisir la personne",
      noPeople: "Connectez-vous d'abord avec un conseiller, un accélérateur ou un fondateur via la Communauté.",
      advisor: "Conseiller",
      entrepreneur: "Entrepreneur",
      pickAdvisor: "Choisir un conseiller",
      pickEntrepreneur: "Choisir un entrepreneur",
      pickFounder: "Choisir un fondateur",
      colleague: "Collègue",
      pickColleague: "Choisir un collègue (optionnel)",
      noColleagues: "Aucun collègue connecté",
      other: "Autre",
      pickOther: "Choisir une autre personne (optionnel)",
      noOthers: "Aucune autre connexion",
      noAdvisors: "Aucun conseiller disponible",
      noEntrepreneurs: "Aucun entrepreneur inscrit",
      noFounders: "Connectez-vous d'abord avec un fondateur via la Communauté.",
      when: "Quand",
      duration: "Durée (min)",
      recurrence: "Récurrence",
      recurrenceNone: "Ne se répète pas",
      recurrenceDaily: "Chaque jour",
      recurrenceWeekly: "Chaque semaine",
      recurrenceWeeklyOn: "Chaque semaine le {day}",
      recurrenceMonthlyOnNth: "Chaque mois le {nth} {day}",
      recurrenceMonthlyOnDay: "Chaque mois le jour {n}",
      recurrenceYearlyOn: "Chaque année le {date}",
      recurrenceWeekdays: "Tous les jours de semaine (lun. au ven.)",
      recurrenceCustom: "Personnalisé…",
      recurrenceCount: "Nombre de rencontres",
      recurrenceEvery: "Répéter tous les",
      recurrenceUnitDay: "jour",
      recurrenceUnitDays: "jours",
      recurrenceUnitWeek: "semaine",
      recurrenceUnitWeeks: "semaines",
      recurrenceUnitMonth: "mois",
      recurrenceUnitMonths: "mois",
      recurrenceUnitYear: "an",
      recurrenceUnitYears: "ans",
      recurrenceOnDays: "Répéter le",
      recurrenceMonthlyMode: "Schéma mensuel",
      recurrenceEnds: "Se termine",
      recurrenceEndsAfter: "Après un nombre de rencontres",
      recurrenceEndsOn: "À une date",
      recurrenceUntil: "Date de fin",
      dowPlaceholder: "ce jour",
      nth: { first: "premier", second: "deuxième", third: "troisième", fourth: "quatrième", last: "dernier" },
      dowShort: { 0: "D", 1: "L", 2: "M", 3: "M", 4: "J", 5: "V", 6: "S" },
      sessionTitle: "Titre (optionnel)",
      template: "Modèle de rencontre",
      rate: "Tarif (par rencontre)",
      currency: "Devise",
      meetingLink: "Lien de rencontre (optionnel, modifiable plus tard)",
      meetingLinkHelp: "Collez une URL Zoom, Teams, Webex ou Meet. Si Google Calendar est connecté, un lien Meet est généré automatiquement. Le bot d'enregistrement fonctionne sur les 4 plateformes.",
      observers: "Inviter des observateurs (optionnel)",
      observersHelp: "Fournisseurs, investisseurs ou accélérateurs en lecture seule.",
      cancel: "Annuler",
      create: "Créer la rencontre",
    },
    settings: {
      title: "Paramètres",
      subtitle: "Gérez votre profil et vos identifiants.",
      profileTitle: "Profil",
      bio: "Bio",
      bioPlaceholder: "Une courte description visible par les conseillers et fondateurs.",
      photoUrl: "Photo",
      photoUpload: "Téléverser une photo",
      photoRemove: "Retirer la photo",
      photoSelected: "Photo téléversée",
      photoTooBig: "Choisissez une image de moins de 8 Mo.",
      photoNotImage: "Choisissez un fichier image.",
      headline: "Titre actuel",
      headlinePlaceholder: "ex. Growth and Strategy Consultant chez Colectiv",
      location: "Lieu",
      locationPlaceholder: "Ville, pays",
      season: "Date",
      linkedin: "Page LinkedIn",
      website: "Site web",
      websitePlaceholder: "https://votre-site.com",
      tagline: "Accroche",
      taglinePlaceholder: "ex. Entrepreneur | Conseiller | Tech, IA et biotech",
      importLinkedInPhoto: "Importer la photo LinkedIn",
      importingPhoto: "Import…",
      photoImported: "Photo importée depuis LinkedIn",
      photoHow: "Téléversez une image, ou ouvrez la photo dans un onglet séparé, copiez l'adresse de l'image, puis collez cette URL ci-dessous.",
      expertiseTitle: "Domaines d'expertise",
      expertiseHint: "Choisissez les domaines sur lesquels vous conseillez. Ces étiquettes apparaissent sur votre carte dans la communauté.",
      bioLangHint: "À l’enregistrement, les champs vides dans l’autre langue sont traduits. Vous pouvez ensuite modifier le résultat.",
      profileText: "Titre, accroche et biographie",
      appLang: "Langue de l’interface",
      appLangHint: "Langue de l’application et des courriels après enregistrement.",
      expertiseDetailLabel: "Détail — {area}",
      expertiseDetailPlaceholder: "Une compétence par ligne, ex. Planification stratégique et feuille de route",
      mediaMentions: "Dans les médias",
      mediaMentionsPlaceholder: "Mentions presse, conférences ou couverture (optionnel)",
      extraLinks: "Autres liens",
      extraLinksHint: "URLs optionnelles affichées à la fin de votre profil public.",
      linkLabel: "Libellé",
      addLink: "Ajouter un lien",
      removeLink: "Retirer",
      composeTitle: "Composer depuis LinkedIn",
      composeHint: "Collez l’URL de votre profil LinkedIn pour remplir la photo, le nom, le titre, la bio, l’entreprise et le lieu.",
      composeWelcome: "Bienvenue. Commencez par votre URL LinkedIn : nous rédigeons une première version de votre profil conseiller.",
      composeApply: "Remplir depuis LinkedIn",
      composing: "Lecture de LinkedIn…",
      composePartial: "Photo et nom remplis. LinkedIn masque le titre et la bio publics — complétez-les ci-dessous.",
      composeLimited: "La fiche publique LinkedIn contient la photo, le nom, l’entreprise, le lieu et un début de bio. Elle coupe la bio et n’inclut pas le Titre actuel ni l’Accroche — ajoutez ces deux champs ci-dessous.",
      directoryToggle: "M'afficher dans le répertoire de la communauté",
      directoryHint: "Lorsque cette option est activée, les autres membres peuvent trouver votre profil public (nom, rôle, entreprise, bio) sous « Découvrir » dans la Communauté.",
      directoryTitle: "Visibilité dans la communauté",
      directoryPublic: "Public — m'afficher dans le répertoire",
      directoryPublicHint: "Tout utilisateur de TractionBoard peut vous trouver dans la Communauté.",
      directoryWhitelist: "Sélectif — m'afficher uniquement à ces courriels",
      directoryWhitelistHint: "Seuls les utilisateurs avec ces adresses courriel voient votre profil dans le répertoire.",
      directoryHidden: "Caché — ne pas m'afficher",
      directoryHiddenHint: "Vous n'êtes visible qu'aux personnes avec qui vous êtes déjà connecté(e).",
      save: "Enregistrer",
      saved: "Enregistré",
      passwordTitle: "Changer le mot de passe",
      currentPassword: "Mot de passe actuel",
      updatePassword: "Mettre à jour",
      passwordUpdated: "Mot de passe mis à jour",
      teamTitle: "Co-fondateurs & équipe",
      teamHint: "Invitez des coéquipiers qui partagent votre entreprise. Ils auront un accès complet en lecture/écriture à vos métriques, roadmap, méthodologies et documents.",
      teamTitleAccelerator: "Collègues de l'accélérateur",
      teamHintAccelerator: "Invitez des collègues sur le compte principal de l'accélérateur. Ils reçoivent un courriel d'invitation et peuvent voir et modifier le portfolio d'entreprises et de coachs. Seul le principal peut importer un CSV.",
      manageTeam: "Gérer l'équipe",
      templates: {
        title: "Modèles de rencontre",
        weekly11Name: "1:1 hebdomadaire",
        hint: "Modèles pour capter l'information avant, pendant et après les rencontres. Les packs de départ couvrent conseiller, accélérateur, investisseur, fournisseur et collègue. Dupliquez un modèle système pour en éditer une copie, ou importez un markdown pour en créer un nouveau.",
        principalOnly: "Les conseillers, accélérateurs, investisseurs, fournisseurs, admins ou le fondateur principal peuvent gérer les modèles de rencontre.",
        empty: "Pas encore de modèles — dupliquez un défaut système pour commencer.",
        default: "Défaut",
        system: "Système",
        edit: "Modifier",
        duplicate: "Dupliquer",
        delete: "Supprimer",
        new: "+ Nouveau modèle",
        titleNew: "Nouveau modèle",
        titleEdit: "Modifier le modèle",
        notFound: "Modèle introuvable.",
        systemCopyHint: "Les modèles système ne peuvent pas être écrasés. Enregistrer crée votre copie.",
        label: "Question",
        prompt: "Invite",
        jumpBasics: "Général",
        jumpBefore: "Avant",
        jumpDuring: "Pendant",
        jumpAfter: "Après",
        jumpAi: "IA",
        qtype: {
          long_text: "Texte long",
          short_text: "Texte court",
          scale_1_10: "Échelle 1–10",
          single_choice: "Choix unique",
          multi_choice: "Choix multiple",
          checklist: "Liste",
        },
        name: "Nom",
        description: "Description",
        markDefault: "Utiliser par défaut",
        type: "Type",
        options: "Options (séparées par des virgules)",
        addQuestion: "+ Ajouter une question",
        removeQuestion: "Retirer",
        confirmDelete: "Supprimer ce modèle ? S'il a déjà été utilisé, il sera archivé.",
        manage: "Gérer les modèles",
        required: "Obligatoire",
        tabAdvisory: "Conseiller",
        tabAccelerator: "Accélérateur",
        tabInvestor: "Investisseur",
        tabProvider: "Fournisseur",
        tabTeammate: "Collègue",
        beforeTitle: "Formulaire avant la rencontre",
        duringTitle: "Invites de notes pendant la rencontre",
        duringHint: "Affichées au-dessus des notes Partager / Privé. Ce sont des invites, pas des champs de formulaire.",
        addDuringPrompt: "+ Ajouter une invite pendant la rencontre",
        afterTitle: "Formulaire après la rencontre",
        afterHint: "Questions de clôture. L'après est enregistré séparément de l'avant.",
        addAfterQuestion: "+ Ajouter une question après rencontre",
        beforeCount: "avant",
        duringCount: "pendant",
        afterCount: "après",
        question: "question",
        questions: "questions",
        whoFounder: "fondateur",
        whoAdvisor: "conseiller",
        whoAccelerator: "accélérateur",
        whoInvestor: "investisseur",
        whoProvider: "fournisseur",
        whoBoth: "les deux",
        whoEach: "chacun",
        countBit: "{n} {word} {phase} ({who})",
        importMd: "Importer du markdown",
        importMdTitle: "Importer un modèle de rencontre depuis un fichier markdown",
        importMdHint: "Comme l'éditeur de méthodologies : copiez le modèle vide, éditez-le, puis collez ou déposez un fichier .md. Les sections Avant / Pendant / Après / Résumé IA deviennent formulaires, invites de notes et direction du résumé. Un markdown invalide renvoie une erreur claire.",
        pickFile: "Choisir un fichier .md",
        pasteMd: "Collez votre markdown ici…",
        importCta: "Importer et ouvrir",
        mdEmpty: "Collez ou choisissez d'abord un fichier markdown.",
        summaryTitle: "Direction du résumé IA",
        summaryHint: "Markdown qui oriente le recap. Les notes privées ne sont jamais envoyées au modèle. Sans direction, le défaut actuel s'applique.",
        summaryPlaceholder: "# Ton\nDirect, opérationnel…",
        preview: "Aperçu",
      },
    },
    team: {
      title: "Co-fondateurs & équipe",
      subtitle: "Invitez vos coéquipiers à collaborer sur votre entreprise. Les membres de l'équipe partagent l'accès aux métriques, feuilles de route, méthodologies, documents et discussions stratégiques.",
      foundersOnly: "Seul le fondateur principal ou l'accélérateur principal peut gérer une équipe.",
      acceleratorTitle: "Collègues de l'accélérateur",
      acceleratorSubtitle: "Invitez des collègues qui partagent cet accélérateur. Ils reçoivent un courriel et sont connectés automatiquement avec vous et avec les autres collègues. Ils peuvent voir et modifier le portfolio d'entreprises et de coachs.",
      acceleratorIntro: "Les collègues rejoignent le compte principal. Les connexions dans Communauté sont envoyées automatiquement. Seul le principal peut importer des contacts par CSV.",
      members: "Membres de l'équipe",
      primary: "Principal",
      cofounder: "Cofondateur",
      cofounderIntro: "Marquez des coéquipiers comme cofondateurs. Les cofondateurs sont automatiquement ajoutés comme approbateurs requis sur chaque nouveau Living Doc — le fondateur principal est toujours cofondateur.",
      cofounderPrimaryHint: "Le fondateur principal est toujours cofondateur.",
      markAsCofounder: "+ Marquer comme cofondateur",
      removeCofounder: "Retirer le statut de cofondateur",
      pendingInvites: "Invitations en attente",
      expires: "Expire le",
      invite: "Inviter un coéquipier",
      sendInvite: "Envoyer l'invitation",
      alreadyMember: "Cette personne fait déjà partie de votre équipe.",
      inviteSent: "Invitation envoyée.",
      lastSent: "Dernier envoi : {when}",
      emailFailed: "L'invitation est enregistrée, mais le courriel n'a pas pu être envoyé. Utilisez Renvoyer.",
      resend: "Renvoyer",
      resent: "Invitation renvoyée.",
      confirmCancel: "Annuler cette invitation en attente ?",
      confirmRemove: "Retirer ce coéquipier ? Il perdra l'accès aux données de votre entreprise.",
      optional: "optionnel",
      acceptTitle: "Rejoindre l'équipe",
      acceptIntro: "{inviter} vous a invité(e) à rejoindre {company} sur TractionBoard.",
      acceptCta: "Accepter l'invitation",
      acceptBusy: "Connexion à l'équipe…",
      acceptSuccess: "Bienvenue ! Redirection vers le tableau de bord partagé…",
      acceptNeedLogin: "Vous devez vous connecter (ou créer un compte) avec {email} pour accepter cette invitation.",
      acceptInvalid: "Ce lien d'invitation est invalide ou a expiré.",
      acceptWrongEmail: "Cette invitation a été envoyée à {email}. Connectez-vous avec cette adresse.",
      invitedAs: "Invité(e) en tant que",
      iHaveAccount: "J'ai déjà un compte",
      iAmNew: "Je suis nouveau — créer mon compte",
      loginAndJoin: "Se connecter et rejoindre",
      createAndJoin: "Créer un compte et rejoindre",
      wrongPassword: "Mot de passe incorrect pour cet email. Réessayez ou créez un compte.",
      noAccountYet: "Pas encore de compte ?",
      haveAccount: "Vous avez déjà un compte ?",
    },
    admin: {
      title: "Admin — gestion des utilisateurs",
      subtitle: "Modifier les rôles, réinitialiser les mots de passe ou supprimer des comptes. Visible uniquement par les administrateurs.",
      search: "Rechercher",
      searchPlaceholder: "Rechercher par nom, courriel ou entreprise…",
      noResults: "Aucun utilisateur ne correspond à cette recherche.",
      actions: "Actions",
      edit: "Modifier",
      delete: "Supprimer",
      save: "Enregistrer",
      editTitle: "Modifier l'utilisateur",
      resetPassword: "Réinitialiser le mot de passe (optionnel)",
      resetPasswordHint: "Laissez vide pour conserver le mot de passe actuel",
      confirmDeleteTitle: "Supprimer ce compte ?",
      confirmDeleteBody: "Vous êtes sur le point de supprimer définitivement",
      confirmDeleteHint: "Ceci supprime l'utilisateur et toutes ses données (snapshots, métriques, roadmap, documents, connexions, invitations). Action irréversible.",
      confirmDeleteCta: "Oui, supprimer définitivement",
    },
    google: {
      title: "Calendrier",
      hint: "Vous n'avez pas besoin de connecter un calendrier. TractionBoard a le sien. Si vous le souhaitez, vous pouvez connecter Google ou Microsoft.",
      connectCta: "Connecter Google",
      connected: "Google connecté",
      disconnect: "Déconnecter",
      confirmDisconnect: "Déconnecter Google ? Les futures rencontres ne seront pas ajoutées à ce calendrier.",
      errorGeneric: "Connexion Google impossible. Veuillez réessayer.",
      notConfigured: "Le calendrier Google n'est pas encore configuré sur ce serveur.",
      missingCalendarScope: "Permission Agenda non accordée. Cliquez Déconnecter puis Connecter à nouveau, et assurez-vous d'autoriser « Voir et modifier les événements » quand Google le demande.",
      optional: "Vous n'avez pas besoin de connecter un calendrier. TractionBoard a le sien. Si vous le souhaitez, vous pouvez connecter Google ou Microsoft.",
    },
    microsoft: {
      connectCta: "Connecter le calendrier Microsoft",
      connected: "Calendrier Microsoft connecté",
      disconnect: "Déconnecter",
      confirmDisconnect: "Déconnecter le calendrier Microsoft ? Les futures rencontres ne seront pas ajoutées à ce calendrier.",
      errorGeneric: "Connexion au calendrier Microsoft impossible. Veuillez réessayer.",
      missingCalendarScope: "Permission calendrier non accordée. Cliquez Déconnecter puis Connecter à nouveau, et autorisez l'accès au calendrier quand Microsoft le demande.",
      notConfigured: "Le calendrier Microsoft n'est pas encore configuré sur ce serveur.",
    },
    mandates: {
      title: "Mandats",
      subtitle: "Un mandat relie un fondateur et un conseiller. Il fixe la durée, la fréquence, le début et la fin des rencontres, ainsi que le coût s'il y en a. Le conseiller a alors accès au portfolio du fondateur.",
      notReady: "Les mandats ne sont pas prêts. Planifiez une rencontre depuis Bookings pour l'instant.",
      emptyHidden: "Rien ici pour le moment.",
      advisorEntrepreneurOnly: "Les mandats lient un conseiller et un entrepreneur.",
      newMandate: "Nouveau mandat",
      counterparty: "Contrepartie",
      pickCounterparty: "Choisir une contrepartie",
      noConnections: "Vous n'avez pas encore de connexion qualifiante — connectez-vous via",
      titleLabel: "Titre",
      titlePlaceholder: "ex. : Stratégie GTM & exécution 90 jours",
      scope: "Portée / description",
      scopePlaceholder: "Ce qui est livré, quand, et comment le succès est mesuré.",
      currency: "Devise",
      totalAmount: "Montant total",
      startDate: "Date de début",
      endDate: "Date de fin",
      duration: "Durée des rencontres (minutes)",
      frequency: "Fréquence",
      freq: { weekly: "Chaque semaine", biweekly: "Toutes les deux semaines", monthly: "Chaque mois" },
      cost: "Coût à la charge du fondateur",
      confirmRequest: "Demander une confirmation",
      confirmHint: "Désactivé par défaut. La connexion dans Communauté et l’accès du conseiller au portfolio sont acceptés automatiquement.",
      send: "Envoyer le mandat",
      bookFirst: "Prendre le premier rendez-vous",
      bookNext: "Planifier une autre rencontre",
      createDraft: "Créer le brouillon",
      empty: "Aucun mandat. Cliquez sur « Nouveau mandat » pour en créer un.",
      youAreAdvisor: "Vous êtes le conseiller",
      youAreFounder: "Vous êtes le fondateur",
      youAreAccelerator: "Créé par votre accélérateur",
      advisor: "Conseiller",
      entrepreneur: "Fondateur",
      milestones: "Jalons",
      noMilestones: "Aucun jalon ajouté.",
      addMilestone: "Ajouter un jalon",
      milestoneTitle: "Titre du jalon",
      milestoneDescription: "Description (à quoi ressemble le succès)…",
      editMilestones: "Modifier les jalons",
      edit: "Modifier",
      save: "Enregistrer",
      summary: "Résumé",
      actions: "Actions",
      propose: "Proposer à la contrepartie",
      accept: "Accepter",
      decline: "Refuser",
      declineReason: "Motif (optionnel)",
      cancelMandate: "Annuler le mandat",
      confirmCancel: "Annuler ce mandat ? Les deux parties seront notifiées.",
      markComplete: "Marquer comme terminé",
      due: "Échéance",
      deliveredOn: "Livré le",
      approvedOn: "Approuvé le",
      viewDeliverable: "Voir le livrable",
      startMilestone: "Démarrer",
      deliverMilestone: "Livrer",
      approveMilestone: "Approuver",
      deliverableUrl: "Lien du livrable (optionnel)",
      deliverableNote: "Note pour le fondateur (optionnel)",
      submitDelivery: "Soumettre la livraison",
      status: {
        draft: "Brouillon",
        proposed: "Proposé",
        accepted: "Accepté",
        declined: "Refusé",
        active: "Actif",
        completed: "Terminé",
        cancelled: "Annulé",
      },
      milestoneStatus: {
        pending: "En attente",
        in_progress: "En cours",
        delivered: "Livré",
        approved: "Approuvé",
        paid: "Payé",
      },
    },
    mit: {
      progress: "Progression",
      todo: "Non démarré",
      in_progress: "En cours",
      validated: "Validé",
      deliverable: "Livrable",
      deliverablePlaceholder: "Lien, fichier ou résumé de ce qui a été produit…",
      metric: "Métrique de succès",
      gate: "Critère d'avancement",
      saveStep: "Enregistrer l'étape",
      placeholder: "Votre réponse…",
    },
    documents: {
      title: "Documents",
      subtitleOwn: "Joignez des pitch decks, plans d'affaires, term sheets, modèles financiers — toute URL ou fichier pertinent pour votre entreprise. Visible par vos conseillers / investisseurs / accélérateurs connectés.",
      subtitleViewer: "Documents partagés par ce fondateur. Cliquez sur un élément pour l'ouvrir ou le télécharger.",
      add: "Ajouter un document",
      modeUrl: "Lien",
      modeFile: "Téléverser",
      titleField: "Titre",
      titlePlaceholder: "Pitch deck v3, modèle financier 2026…",
      urlField: "URL",
      urlRequired: "L'URL est requise",
      fileField: "Fichier",
      fileHint: "Max 8 Mo. PDF, slides, docs, images.",
      fileRequired: "Veuillez choisir un fichier",
      fileEmpty: "Ce fichier est vide",
      titleRequired: "Le titre est requis",
      downloadFailed: "Impossible de télécharger ce fichier",
      tooLarge: "Le fichier dépasse 8 Mo",
      save: "Enregistrer",
      empty: "Aucun document pour l'instant.",
      confirmRemove: "Supprimer ce document ?",
    },
    portfolio: {
      title: "Portfolio",
      subtitleFounder: "L'instantané de votre entreprise. Modifiez vos Métriques, Feuille de route et Méthodologie, puis « Enregistrer & soumettre » pour partager la dernière version avec vos connexions.",
      meetings: "Rencontres",
      meetingsEmpty: "Aucune rencontre pour le moment.",
      notes: "Notes",
      notesEmpty: "Aucune note pour le moment.",
      notesHint: "Chacun ne voit que les notes qui lui sont destinées.",
      noteMeeting: "Rencontre",
      meetingsUpcoming: "À venir",
      meetingsPast: "Passées",
      subtitleFunnel: "Suivez les fondateurs avec qui vous travaillez à travers votre propre pipeline. Personnalisez les étapes, puis ajoutez des fondateurs depuis vos connexions de la communauté.",
      subtitleAccelerator: "Deux tableaux : les entreprises que vous accompagnez, et les coachs/conseillers qui contribuent.",
    },
    funnel: {
      editStages: "Modifier les étapes",
      saveStages: "Enregistrer les étapes",
      addStage: "Ajouter une étape",
      removeStage: "Supprimer",
      empty: "Vide",
      addFounder: "Ajouter un fondateur",
      addAdvisor: "Ajouter un conseiller",
      pickFounder: "Choisir un fondateur",
      pickAdvisor: "Choisir un conseiller",
      noConnections: "Connectez-vous d'abord avec un fondateur via l'onglet Communauté.",
      allInFunnel: "Toutes les entreprises de vos connexions sont déjà sur le tableau.",
      unassignedHint: "Certaines personnes de vos connexions ne sont pas encore dans votre pipeline — ajoutez-les, ou visitez",
      addNotes: "Ajouter des notes",
      editNotes: "Modifier les notes",
      notesPlaceholder: "Mandat, prochaine étape, dernier contact…",
      confirmRemove: "Retirer ce fondateur de votre pipeline ?",
      filterApply: "Appliquer",
      foundersBoard: "Entreprises",
      addCompany: "Ajouter une entreprise",
      pickCompany: "Choisir une entreprise",
      advisorsBoard: "Coachs / Conseillers",
      dataManagement: "Gestion des données",
      cohort: "Cohorte",
      year: "Année",
      archive: "Archiver",
      downloadTemplate: "Télécharger le modèle CSV",
      downloadExport: "Télécharger le CSV des fondateurs",
      importTitle: "Importer coachs et entrepreneurs",
      importPrincipalOnly: "Seul l'accélérateur principal peut importer un CSV. Vous pouvez tout de même voir et modifier le portfolio.",
      exportTitle: "Exporter les données des entrepreneurs",
      importPlaceholder: "Collez un CSV ou choisissez un fichier…",
      importSubmit: "Importer le CSV",
      importResult: "Lignes importées : {n}. Lignes invalides : {e}. Dernière importation : {file}, {when}",
      importPasted: "CSV collé",
      dismissImport: "Fermer",
      manualAdd: "Ou saisir une personne",
      manualName: "Nom complet",
      manualEmail: "Courriel",
      manualCompany: "Entreprise",
      manualSubmit: "Ajouter",
      exportFailed: "L'export n'a pas abouti. Réessayez.",
      health: "Santé de l’entreprise",
      healthSet: "Indiquer la santé",
      healthGreen: "Sur la bonne voie",
      healthYellow: "À surveiller",
      healthOrange: "Quelques enjeux",
      healthRed: "Enjeu majeur",
    },
    community: {
      title: "Communauté",
      subtitle: "Connectez-vous avec des fondateurs, conseillers, gestionnaires de fonds, fournisseurs de services et accélérateurs.",
      subtitle: "Connectez-vous avec des fondateurs, conseillers, gestionnaires de fonds, fournisseurs et accélérateurs sur TractionBoard.",
      mineTitle: "Mes connexions",
      mineSubtitle: "Les personnes avec qui vous êtes explicitement connecté. Elles voient vos Métriques, Feuille de route et Méthodologies lors de « Enregistrer & soumettre ».",
      directoryTitle: "Découvrir",
      directorySubtitle: "Les membres qui ont choisi de figurer dans le répertoire public.",
      pendingIn: "Demandes reçues",
      pendingOut: "Demande envoyée",
      accept: "Accepter",
      connect: "Se connecter",
      editProfile: "Modifier",
      connected: "Connecté",
      empty: "Personne pour l'instant.",
      backTo: "Retour à la Communauté",
      lockedHint: "Connectez-vous avec ce fondateur pour voir ses Métriques, Feuille de route et Méthodologies.",
      disconnect: "Se déconnecter",
      confirmDisconnect: "Se déconnecter de cette personne ? Vous perdrez tous les deux l'accès aux données partagées.",
      searchPlaceholder: "Rechercher par nom, entreprise ou expertise…",
      allExpertises: "Toutes les expertises",
      filterAllMembers: "Tous les membres",
      filterAllFounders: "Tous les fondateurs",
      filterAllAdvisors: "Tous les conseillers",
      filterAllProviders: "Tous les fournisseurs",
      filterAllInvestors: "Tous les investisseurs",
      filterAllAccelerators: "Tous les accélérateurs",
      linkedin: "Profil LinkedIn",
      meetings: "Rencontres",
      meetingsSubtitle: "Rencontres avec ce fondateur. Ouvrez une session pour voir les notes, transcriptions et résumés.",
      expertiseDetails: "Détail des expertises",
      inTheMedia: "Dans les médias",
      languages: "Langues",
      contact: "Contact",
      links: "Liens",
      recommend: "Recommander ce conseiller",
      recommended: "Recommandé",
      recommendCount: "{n} recommandations",
      recommendCountOne: "1 recommandation",
      recommendCountZero: "Aucune recommandation",
    },
    expertise: {
      strategy: "Stratégie et développement d'entreprise",
      marketing: "Marketing et communications",
      sales: "Ventes et développement commercial",
      finance: "Finances et financement",
      legal: "Légal et gouvernance",
      hr: "Ressources humaines et organisation",
      tech: "Technologie et innovation",
      operations: "Opérations et logistique",
      impact: "Impact et développement durable",
    },
    roles: {
      founders: "Fondateurs",
      founder: "Fondateur",
      advisors: "Conseillers",
      advisor: "Conseiller",
      providers: "Fournisseurs",
      provider: "Fournisseur",
      fundManagers: "Gestionnaires de fonds",
      fundManager: "Gestionnaire de fonds",
      accelerators: "Accélérateurs",
      accelerator: "Accélérateur",
      members: "Membres",
      admin: "Admin",
    },
    snapshot: {
      metricsLabel: "Métriques — dernière version",
      roadmapLabel: "Feuille de route — dernière version",
      frameworkLabel: "Méthodologie — dernière version",
      noneYet: "Pas encore soumise",
      saveSubmit: "Enregistrer & soumettre",
      viewHistory: "Voir l'historique",
      pickOne: "Choisissez une version à afficher.",
      historySubtitle: "Chaque « Enregistrer & soumettre » crée un instantané immuable. La dernière version est celle partagée avec vos connexions.",
      history_metrics: "Métriques — historique",
      history_roadmap: "Feuille de route — historique",
      history_framework: "Méthodologie — historique",
    },
    terms: {
      title: "Modalités de la rencontre",
      when: "Quand",
      duration: "Durée",
      rate: "Tarif",
      advisor: "Conseiller",
      entrepreneur: "Fondateur",
      pending: "En attente de confirmation",
      bothConfirmed: "Confirmé par les deux parties",
      confirm: "Confirmer",
      proposeChanges: "Proposer des changements",
      propose: "Envoyer la proposition",
      cancelMeeting: "Annuler la rencontre",
      cancelConfirm: "Annuler cette rencontre ? L'autre partie sera notifiée, l'invitation calendrier supprimée et le bot d'enregistrement automatique arrêté.",
      deleteMeeting: "Supprimer",
      deleteConfirm: "Supprimer cette rencontre entièrement ? L'autre partie n'a pas encore confirmé, aucun historique ne sera conservé.",
      archive: "Archiver",
      unarchive: "Désarchiver",
      inviteSent: "Invitation envoyée",
      inviteNotSent: "Invitation pas encore envoyée",
      cancelSent: "Annulation envoyée",
      resendInvite: "Renvoyer l'invitation",
      cancelPromptReason: "Motif (optionnel) :",
      cancelled: "Annulée",
    },
    detail: {
      back: "Retour",
      joinMeeting: "Joindre la rencontre",
      addMeetingLink: "Ajouter un lien",
      updateMeetingLink: "Mettre à jour le lien",
      pasteLinkPlaceholder: "https://meet.google.com/... · zoom.us/j/... · teams.microsoft.com/...",
      linkHelp: "Collez une URL Zoom, Teams, Webex ou Google Meet. Si TractionBoard a généré un lien Meet automatiquement, il apparaît ici — gardez-le ou remplacez-le.",
      save: "Enregistrer",
      preTitle: "Check-in pré-rencontre",
      preForEntrepreneur: "À remplir avant la rencontre. Votre conseiller pourra le consulter avant l'appel.",
      preForAdvisor: "Check-in pré-rencontre du fondateur.",
      preNotYet: "L'entrepreneur n'a pas encore soumis son check-in.",
      happiness: "Niveau de bonheur (1–10)",
      scaleRequired: "Choisissez une note de 1 à 10.",
      happinessReason: "Pourquoi ce score ?",
      challenges: "Top 5 défis",
      opportunities: "Top 5 opportunités",
      priorities: "Top 5 priorités",
      priority30d: "Priorité #1 des 30 prochains jours",
      priority30dReason: "Pourquoi est-ce critique ?",
      limitingFactor: "Principal facteur limitatif actuel",
      objective: "Détails additionnels",
      blockerCategory: "Qu'est-ce qui bloque / ralentit ?",
      blockerDetails: "Détails",
      blockers: {
        resources: "Ressources",
        sales: "Ventes",
        product: "Produit",
        team: "Équipe",
        clarity: "Clarté stratégique",
        execution: "Exécution",
        other: "Autre",
      },
      submitPre: "Soumettre le check-in",
      preSubmitted: "Soumis",
      postTitle: "Post-rencontre",
      postForAdvisor: "À remplir après la rencontre. TractionBoard génère ensuite le résumé.",
      postForOther: "Notes post-rencontre de l’autre personne.",
      postNotYet: "Le formulaire post-rencontre n’a pas encore été soumis.",
      recommendations: "Recommandations (actions concrètes, 7 jours)",
      biggestRisk: "Plus gros risque à surveiller",
      objective1: "Objectif #1 (30 prochains jours)",
      metric: "Métrique associée",
      owner: "Responsable",
      watchpoint: "Point de vigilance / signal de dérapage",
      deadline: "Échéance",
      advisorNotes: "Notes additionnelles",
      transcript: "Verbatim de la rencontre (optionnel)",
      transcriptHelp: "Collez le verbatim pour améliorer le résumé IA.",
      fullTranscript: "Verbatim de la rencontre",
      refreshTranscript: "Actualiser",
      transcriptPending: "Le verbatim apparaîtra ici automatiquement quelques minutes après la fin de la rencontre.",
      transcriptStatus: {
        pending: "En attente",
        scheduled: "Bot planifié",
        joining: "Le bot rejoint",
        in_call: "Enregistrement",
        recording_done: "Traitement",
        available: "Disponible",
        failed: "N'assistera pas",
        fatal: "N'assistera pas",
        not_configured: "Enregistreur désactivé",
        skipped_optout: "Enregistrement désactivé",
        no_meeting_url: "En attente du lien",
        done: "Terminé",
      },
      recall: {
        notAttendingTitle: "L'enregistreur TractionBoard n'est pas planifié pour assister à cette rencontre.",
        dispatchFailed: "Le bot n'a pas pu être planifié. Il s'agit généralement d'un solde de crédits Recall.ai ou d'une clé API côté plateforme. Contactez votre administrateur (ou utilisez Réessayer une fois le problème résolu).",
        notConfigured: "Le bot de transcription n'est pas configuré sur cet environnement. Contactez l'administrateur pour activer Recall.ai.",
        skippedOptout: "L'enregistrement est désactivé pour cette session. Réactivez-le (et cliquez sur Réessayer) pour planifier l'enregistreur.",
        noMeetingUrl: "Aucun lien de rencontre n'est défini. Ajoutez une URL Meet/Zoom pour planifier l'enregistreur.",
        showDetails: "Voir les détails techniques",
        retryScheduleBot: "Réessayer",
        retryFailed: "La nouvelle tentative a échoué — voir les détails.",
      },
      hide: "Masquer",
      show: "Afficher",
      submitPost: "Soumettre & générer le résumé",
      summaryTitle: "Résumé généré par IA",
      summaryNotYet: "Le résumé apparaîtra ici après le formulaire post-rencontre.",
      regenerate: "Regénérer le résumé",
      addOne: "+ Ajouter",
      removeOne: "Retirer",
      mandatory: "Requis",
    },
    notif: {
      title: "Notifications",
      none: "Aucune notification.",
      markAllRead: "Tout marquer lu",
    },
    common: {
      loading: "Chargement…",
      error: "Erreur",
      retry: "Réessayer",
      back: "Retour",
      cancel: "Annuler",
      save: "Enregistrer",
      saving: "Enregistrement…",
      delete: "Supprimer",
      create: "Créer",
      close: "Fermer",
    },
    framework: {
      title: "Méthodologies",
      subtitle: "Méthodologies de maturité standardisées (KTH Innovation Readiness Level, cycle de vie d'entreprise, …) pour évaluer où l'entreprise se situe aujourd'hui et ce qui vient ensuite. Les fondateurs s'auto-évaluent ; les conseillers obtiennent un modèle mental commun pour ancrer les échanges.",
      empty: "Aucune méthodologie — un admin peut en ajouter une.",
      yourStage: "Votre étape actuelle",
      assignedTitle: "Méthodologies assignées",
      chooseStageHint: "Choisissez votre étape en fonction du tableau plus bas.",
      kthSelfTitle: "Auto-évaluation : où en êtes-vous sur chaque axe ?",
      kthSelfHelp: "Choisissez un niveau (1–9) pour chaque axe. Chaque maturité avance indépendamment.",
    },
    metrics: {
      title: "Métriques",
      subtitle: "Suivez les 10 métriques de base. Ajoutez ou retirez les vôtres.",
      coreMetrics: "Métriques de base",
      customMetrics: "Métriques personnalisées",
      add: "Ajouter",
      label: "Libellé",
      key: "Clé (slug)",
      unit: "Unité",
      noCustom: "Aucune métrique personnalisée.",
      confirmDelete: "Supprimer la métrique",
      openGroups: "Groupes de métriques →",
    },
    metricGroups: {
      title: "Groupes de métriques",
      subtitle: "Créez, partagez et suivez des tableaux de bord. Combinez plusieurs métriques en bundles adaptés à chaque équipe ou partie prenante.",
      newGroup: "Nouveau groupe",
      backToMetrics: "← Retour aux métriques",
      backToList: "Retour à la liste",
      edit: "Modifier",
      assign: "Assigner",
      assigned: "Assigné",
      unassign: "Retirer",
      track: "Saisir des valeurs →",
      viewValues: "Voir les valeurs",
      assignedBy: "Assigné par",
      targetUser: "Cible",
      itemsCount: "{count} métrique(s)",
      confirmDelete: "Supprimer ce groupe de métriques ? Les assignations seront aussi supprimées.",
      emptyMine: "Vous n'avez pas encore créé de groupe de métriques.",
      emptyAssigned: "Aucun groupe ne vous a été assigné pour l'instant.",
      emptyShared: "Vous n'avez encore partagé aucun groupe.",
      tabs: { mine: "Les miens", assigned: "Assignés à moi", shared: "Partagés par moi" },
      assignTitle: "Assigner le groupe",
      assignSubtitle: "Sélectionnez une ou plusieurs de vos connexions. Elles pourront saisir les valeurs de ce groupe.",
      currentAssignees: "Actuellement assignés",
      yourConnections: "Vos connexions",
      noConnections: "Aucune connexion pour l'instant. Connectez-vous avec des fondateurs ou advisors d'abord.",
      frequency: { daily: "Quotidien", weekly: "Hebdomadaire", monthly: "Mensuel", quarterly: "Trimestriel", yearly: "Annuel" },
      types: { number: "Nombre", currency: "Devise", percent: "Pourcent", text: "Texte" },
      builder: {
        createTitle: "Créer un groupe de métriques",
        editTitle: "Modifier le groupe de métriques",
        subtitle: "Nommez le groupe, puis ajoutez les métriques à suivre. Chaque métrique a sa propre fréquence de mise à jour.",
        nameEn: "Nom (EN)",
        nameFr: "Nom (FR)",
        description: "Description",
        metrics: "Métriques de ce groupe",
        addExisting: "Ajouter une métrique existante",
        createNew: "Créer une nouvelle métrique",
        noMetrics: "Aucune métrique dans ce groupe. Ajoutez-en pour commencer.",
        nameRequired: "Le nom (EN) est requis.",
        atLeastOne: "Ajoutez au moins une métrique avant de sauvegarder.",
      },
      newDef: {
        title: "Créer une nouvelle définition de métrique",
        key: "Clé (slug, minuscules, sans espace)",
        labelEn: "Libellé (EN)",
        labelFr: "Libellé (FR)",
        unit: "Unité",
        type: "Type",
        description: "Description (optionnel)",
      },
      picker: {
        title: "Choisissez une métrique à ajouter",
        search: "Recherche par nom, clé ou libellé EN…",
        empty: "Aucune métrique correspondante. Créez-en une nouvelle.",
      },
      track: {
        value: "Valeur",
        saved: "✓ Enregistré",
        history: "Historique récent",
      },
      view: {
        subtitle: "Dernières valeurs déclarées pour ce groupe.",
        lastUpdate: "Dernière mise à jour",
        noData: "Aucune donnée — la personne assignée n'a encore rien saisi.",
        history: "Historique récent",
      },
    },
    surveys: {
      title: "Sondages & Feedback",
      subtitle: "Aidez-nous à façonner TractionBoard en partageant votre réalité. Environ 5 minutes chacun.",
      empty: "Aucun sondage assigné pour l'instant.",
      questions: "questions",
      back: "Retour aux sondages",
      start: "Commencer",
      continue: "Continuer",
      review: "Revoir",
      submit: "Soumettre",
      saveExit: "Sauvegarder et quitter",
      thanks: "Merci !",
      thanksMsg: "Vos réponses orientent directement ce qu'on bâtit ensuite.",
      backToApp: "Retour à l'app",
      other: "Autre (précisez)",
      upTo: "Choisissez jusqu'à {n}",
      allThatApply: "Toutes les réponses applicables",
      rankHint: "Classez votre top {n} (1 = la plus importante)",
      promptTitle: "Aidez-nous à améliorer TractionBoard",
      promptDuration: "Durée estimée : {n} minutes",
      startNow: "Commencer le sondage",
      skip: "Plus tard",
      status: { not_started: "Non commencé", in_progress: "En cours", completed: "Complété", dismissed: "Ignoré" },
    },
    roadmap: {
      title: "Feuille de route",
      subtitle: "Tout part d'une étoile polaire, jusqu'aux 2 prochaines semaines.",
      northStar: "Étoile polaire",
      northStarHelp: "L'ambition audacieuse et irrationnelle qui décide tout.",
      year10: "Objectif 10 ans",
      year5: "Objectif 5 ans",
      year2: "Objectif 2 ans",
      year1: "Objectif 1 an",
      quarters: "4 prochains trimestres",
      month1: "30 prochains jours",
      week2: "2 prochaines semaines",
      save: "Enregistrer",
      saved: "Enregistré",
    },
    toolbox: {
      title: "Boîte à outils",
      subtitle: "Services, offres et solutions pour enrichir votre démarche d'accompagnement. À venir.",
      comingSoon: "À venir",
      live: "Disponible",
      frameworksTitle: "Méthodologies",
      frameworksBody: "Parcourez, utilisez ou créez des méthodologies structurées (sections, items, questions) pour guider les diagnostics et l'exécution des fondateurs.",
      servicesTitle: "Services",
      servicesBody: "Services d'experts à la demande — finance, juridique, croissance, opérations — accessibles via votre conseiller.",
      offeringsTitle: "Offres",
      offeringsBody: "Programmes et méthodologies préassemblés pour accélérer des résultats précis.",
      solutionsTitle: "Solutions",
      solutionsBody: "Offres de fournisseurs — produits, tarifs, et comment ils aident les fondateurs.",
      offersServicesTitle: "Offres et services",
      offersServicesBody: "Solutions de fournisseurs, programmes packagés et services d'experts, au même endroit.",
      agentsTitle: "Agents",
      agentsBody: "Copilotes internes branchés sur vos modèles de rencontre, notes et résumés.",
    },
    agents: {
      meetingCopilot: "Copilote de rencontre",
      meetingCopilotBody: "Préparation, notes pendant la rencontre (partagées ou privées), et recaps IA dirigés par votre markdown.",
      useBefore: "Avant : remplir le formulaire de préparation du modèle.",
      useDuring: "Pendant : capturer des notes partagées ou privées.",
      useAfter: "Après : bilan pour les décisions et responsables.",
      useSummary: "Résumé : générer un recap avec votre markdown de direction (jamais les notes privées).",
      yourTemplates: "Vos modèles de rencontre",
      hasSummaryDir: "a une direction IA",
    },
    solutions: {
      title: "Solutions",
      subtitle: "Parcourez les offres des fournisseurs. Chaque page est un one-pager que le fournisseur peut modifier.",
      empty: "Aucune solution publiée pour le moment.",
      backTo: "Toutes les solutions",
      edit: "Modifier cette page",
      editMine: "Modifier ma page solution",
      editTitle: "Page solution",
      editHint: "C'est l'offre que voient les fondateurs : entreprise, deux produits (ou plus), tarifs, et pourquoi vous choisir.",
      viewLive: "Voir la page",
      viewOffering: "Voir la solution",
      providerKicker: "Fournisseur de services",
      contact: "Contacter",
      website: "Site web",
      company: "Entreprise",
      tagline: "Accroche",
      headline: "Titre",
      contactEmail: "Courriel de contact",
      problem: "Le risque",
      howWeHelp: "Comment nous aidons",
      products: "Produits",
      productN: "Produit {n}",
      productName: "Nom du produit",
      productDescription: "Description",
      covers: "Conçu pour",
      coversHint: "Qui / quoi ce produit couvre (une ligne par item)",
      bulletsHint: "Capacités (une ligne par item)",
      onePerLine: "Un item par ligne",
      pricingSummary: "Tarif — court",
      pricingDetails: "Tarif — détails",
      addProduct: "Ajouter un produit",
      whyChoose: "Pourquoi les clients nous choisissent",
      reasonN: "Raison {n}",
      reasonTitle: "Titre",
      reasonBody: "Détail",
      addReason: "Ajouter une raison",
      remove: "Retirer",
      published: "Publier dans le répertoire Solutions",
      save: "Enregistrer la page solution",
      providersOnly: "Seuls les fournisseurs de services peuvent modifier une page solution.",
      stats: "Chiffres clés",
      statN: "Chiffre {n}",
      statValue: "Nombre",
      statLabel: "Ce que ça signifie",
      addStat: "Ajouter un chiffre",
      workflow: "Comment ça fonctionne",
      workflowHint: "Étapes du flux (une par ligne)",
      compliance: "Contribue à",
      complianceHint: "Cadres de conformité (un par ligne)",
      proof: "En production",
      proofN: "Preuve {n}",
      addProof: "Ajouter une preuve",
      coversLabel: "Titre de liste (ex. Pour protéger / Menaces évaluées)",
    },
    fwList: {
      title: "Méthodologies",
      subtitle: "Un diagnostic réutilisable, organisé en sections, items et questions, dont on suit la progression. Conseillers, accélérateurs, gestionnaires de fonds et fournisseurs de services peuvent créer les leurs.",
      create: "Créer une méthodologie",
      import: "Importer une méthodologie",
      mine: "Mes méthodologies",
      mineEmpty: "Vous n'avez pas encore créé de méthodologie.",
      shared: "Partagées avec vous",
      sharedEmpty: "Aucune méthodologie ne vous a encore été partagée.",
      library: "Bibliothèque",
      system: "Système",
      by: "Par",
      edit: "Modifier",
    },
    fwImport: {
      title: "Importer une méthodologie",
      subtitle: "Collez une méthodologie rédigée selon notre modèle. Nous l'analysons et la traduisons automatiquement dans l'autre langue.",
      step1Title: "Étape 1 — Obtenir le modèle",
      step1Hint: "Copiez ou téléchargez ce modèle, puis collez-le dans ChatGPT / Claude / Gemini et demandez au LLM de le remplir sur votre sujet.",
      copy: "Copier le modèle",
      download: "Télécharger .md",
      step2Title: "Étape 2 — Coller le modèle rempli",
      step2Hint: "Collez ici le modèle complété. Nous parserons les sections, items, questions et références. L'autre langue est traduite automatiquement à l'importation.",
      pastePlaceholder: "# Ma méthodologie\n\nDescription: …\n\n## Section 1\n\n### Item 1\nQuestion: …\n",
      importCta: "Importer et ouvrir",
      importing: "Importation…",
      empty: "Collez un modèle rempli avant d'importer.",
    },
    fwBuilder: {
      title: "Constructeur de méthodologie",
      titleNew: "Créer une méthodologie",
      titleEdit: "Modifier la méthodologie",
      editingIn: "Édition en",
      langHint: "Les modifications s'appliquent à la langue affichée ci-dessus. Basculez le sélecteur EN/FR global pour modifier l'autre langue. L'autre langue est remplie automatiquement à la première sauvegarde.",
      roleNotAllowed: "Seuls les conseillers, accélérateurs, gestionnaires de fonds, fournisseurs de services et admins peuvent créer des méthodologies.",
      notFound: "Méthodologie introuvable ou non visible pour vous.",
      basics: "Informations de base",
      titleLabel: "Titre",
      titleRequired: "Le titre est requis.",
      slug: "Identifiant URL",
      slugRequired: "L'identifiant est requis.",
      descLabel: "Description",
      visibility: "Visibilité",
      visPrivate: "Privée (moi & admins uniquement)",
      visConnections: "Partagée avec mes connexions",
      visPublic: "Publique (tout TractionBoard)",
      sections: "Sections",
      addSection: "Ajouter une section",
      noSections: "Aucune section. Ajoutez-en une pour commencer.",
      sectionName: "Nom de la section",
      addItem: "Ajouter un item",
      itemName: "Nom de l'item",
      questions: "Questions",
      addQuestion: "Ajouter une question",
      questionLabel: "Question",
      references: "Références",
      referencesHint: "Liens vers les sources, livres ou articles soutenant cette méthodologie. Affichés à la fin.",
      addReference: "Ajouter une référence",
      refLabel: "Étiquette (optionnel)",
      legalNotice: "Mention légale",
      legalLabel: "Mention légale",
      legalHint: "Avis de non-responsabilité ou de droit d'auteur. Affiché tout à la fin de la méthodologie.",
      save: "Enregistrer",
      deleteFramework: "Supprimer la méthodologie",
      confirmDeleteFramework: "Supprimer cette méthodologie ? Toutes les réponses des fondateurs liées seront aussi supprimées.",
      confirmDeleteSection: "Supprimer cette section et tous ses items ?",
      confirmDeleteItem: "Supprimer cet item et ses questions ?",
    },
    journal: {
      title: "Journal",
      subtitle: "L'espace de réflexion partagé de votre équipe. Capturez vos gains, décisions, blocages — le Brain organise le reste.",
      inputPlaceholder: "Qu'avez-vous en tête ? (Cmd+Entrée pour publier)",
      post: "Publier",
      posting: "Publication…",
      empty: "Rien ici pour l'instant. Tapez votre première pensée au-dessus.",
      emptyForRole: "Le Journal est l'espace de travail quotidien de votre équipe. Seuls les fondateurs et leur équipe peuvent y écrire.",
      loadMore: "Voir plus",
      loading: "Chargement…",
      visibility: {
        private: "Privé",
        team: "Équipe",
        team_advisor: "Équipe + Conseiller",
        team_advisor_investor: "Équipe + Conseiller + Investisseur",
      },
      visibilityHint: {
        private: "Vous seul voyez ceci",
        team: "Tous les cofondateurs voient ceci",
        team_advisor: "Cofondateurs + conseiller assigné",
        team_advisor_investor: "Cofondateurs + conseiller + investisseurs",
      },
      source: {
        native: "écrit ici",
        telegram: "via Telegram",
        imported_llm: "importé d'une conversation IA",
        brain: "du Brain",
        system: "système",
      },
      actions: {
        edit: "Modifier",
        delete: "Supprimer",
        save: "Enregistrer",
        cancel: "Annuler",
        editing: "Modification…",
        confirmDelete: "Supprimer cette entrée ? Impossible à annuler.",
      },
      badges: {
        edited: "modifié",
        extracting: "Le Brain lit…",
        proposalsReady: "{count} suggestion(s)",
      },
      relative: {
        justNow: "à l'instant",
        minutesAgo: "il y a {n}m",
        hoursAgo: "il y a {n}h",
        yesterday: "hier",
        daysAgo: "il y a {n}j",
      },
      errors: {
        tooShort: "Écrivez quelque chose d'abord",
        tooLong: "Trop long (max 20000 caractères)",
        generic: "Une erreur est survenue. Réessayez.",
      },
      askBrain: "Demander au Brain",
      viewProposals: "Voir les suggestions",
      importShareLink: "Importer un chat IA",
      importPrompt: "Collez un lien de partage ChatGPT / Claude / Perplexity",
      importNotePrompt: "Note optionnelle sur cette conversation",
    },
    brain: {
      title: "Brain",
      subtitle: "Posez n'importe quelle question sur votre entreprise. Le Brain lit votre journal, métriques, feuille de route et documents.",
      you: "Vous",
      brain: "Brain",
      ask: "Demander",
      thinking: "Réflexion…",
      inputPlaceholder: "Posez une question sur les métriques, décisions, clients, feuille de route… (Cmd+Entrée)",
      newConversation: "Nouvelle conversation",
      noConversations: "Aucune conversation encore.",
      untitled: "Sans titre",
      emptyForRole: "Le Brain est disponible pour les entrepreneurs et leur équipe.",
      starterHint: "Essayez de demander :",
      starters: {
        risks: "Quels sont nos 3 principaux risques actuellement ?",
        recent: "Qu'est-ce qui a changé dans les 2 dernières semaines ?",
        metrics: "Quelles sont nos métriques actuelles ?",
      },
      errors: { generic: "Une erreur est survenue. Réessayez." },
    },
    proposals: {
      title: "Suggestions",
      subtitle: "Le Brain propose des mises à jour à partir de votre journal. Approuvez ce qui est exact, ignorez le reste.",
      loading: "Chargement…",
      empty: "Aucune suggestion en attente. Continuez à écrire dans votre journal.",
      emptyForRole: "Les suggestions sont disponibles pour les entrepreneurs et leur équipe.",
      apply: "Appliquer",
      skip: "Ignorer",
      kinds: {
        metric_delta: "Métrique",
        decision: "Décision",
        roadmap_change: "Feuille de route",
        kpi_note: "Note KPI",
      },
      errors: { generic: "Une erreur est survenue. Réessayez." },
    },
    memory: {
      title: "Mémoire",
      subtitle: "Tout ce que le Brain voit de votre entreprise — avec niveaux d'automatisation, preuves et journal d'audit.",
      loading: "Chargement…",
      lastUpdated: "Mis à jour",
      sections: {
        journal: "Journal",
        metrics: "Métriques",
        roadmap: "Feuille de route",
        documents: "Documents",
        sessions: "Séances de conseil",
        mandates: "Mandats",
        framework: "Méthodologie",
        proposals: "Suggestions extraites",
        brain: "Conversations Brain",
        decisions: "Décisions",
      },
      counts: {
        entries: "entrées",
        items: "éléments",
        pending: "en attente",
        applied: "appliqués",
        skipped: "ignorés",
      },
      viewAll: "Tout voir",
      empty: "Rien ici pour l'instant.",
      auditTitle: "Journal d'audit",
      auditEmpty: "Aucun changement enregistré.",
      auditUndo: "Annuler",
      auditUndone: "Annulé",
      auditConfirmUndo: "Annuler ce changement ? L'état précédent sera restauré.",
      auditWindowExpired: "Fenêtre d'annulation (24 h) expirée.",
      levels: {
        0: "Manuel",
        1: "Suggéré par IA",
        2: "Importé",
        3: "Automatique",
        4: "Calculé",
      },
      levelBadge: "N{n}",
      openLink: "Ouvrir",
      brainScore: "Connaissance de l'entreprise",
      trustNote: "Chaque fait a un propriétaire, une source et une preuve.",
    },
    memoryApply: {
      title: "Appliquer la suggestion",
      subtitle: "Vérifiez avant d'écrire dans la mémoire de l'entreprise.",
      targetLabel: "Où doit aller cette information ?",
      newValue: "Nouvelle valeur",
      unit: "Unité",
      metricName: "Nom de la métrique",
      noteText: "Note",
      tier: "Niveau de la feuille de route",
      tiers: {
        north_star: "Étoile polaire",
        year_10: "10 ans",
        year_5: "5 ans",
        year_2: "2 ans",
        year_1: "1 an",
        quarter_0: "Trimestre 1",
        quarter_1: "Trimestre 2",
        quarter_2: "Trimestre 3",
        quarter_3: "Trimestre 4",
        month_1: "Prochain mois",
        week_2: "2 prochaines semaines",
      },
      newText: "Texte",
      mode: "Mode",
      modeAppend: "Ajouter à l'existant",
      modeReplace: "Remplacer l'existant",
      decisionTitle: "Titre de la décision",
      decisionOwner: "Responsable",
      decisionContext: "Contexte",
      cancel: "Annuler",
      confirm: "Appliquer et enregistrer",
      applying: "En cours…",
      applied: "Appliqué — nouvelle valeur enregistrée.",
    },
    integrations: {
      title: "Intégrations",
      subtitle: "Chaque source de vérité alimentant la mémoire de l'entreprise. La connaissance de confiance commence ici.",
      loading: "Chargement…",
      status: {
        connected: "Connecté",
        coming_soon: "Bientôt disponible",
        disconnected: "Déconnecté",
      },
      type: {
        manual: "Manuel",
        api: "API",
        file: "Fichier",
        ai_extraction: "Extraction IA",
      },
      mode: {
        read_only: "Lecture seule",
        read_write: "Lecture / Écriture",
      },
      empty: "Aucune intégration disponible.",
      groups: {
        connected: "Connectés aujourd'hui",
        comingSoon: "Bientôt disponibles",
      },
    },
    onboarding: {
      title: "Onboarding IA",
      subtitle: "Un partenaire conversationnel qui bâtit la mémoire de votre entreprise, une question à la fois.",
      foundersOnly: "Cette expérience est réservée aux fondateurs.",
      welcomeTitle: "Rencontrez votre Company Brain",
      welcomeBody: "Au lieu de remplir des formulaires, je vais vous interviewer comme un partenaire d'affaires et construire progressivement une mémoire vivante — stratégie, clients, métriques et priorités. Environ 10 minutes ; vous pouvez faire pause à tout moment.",
      start: "Commencer la conversation",
      starting: "Démarrage…",
      you: "Vous",
      brain: "Brain",
      send: "Envoyer",
      sending: "Envoi…",
      placeholder: "Écrivez votre réponse… (Entrée pour envoyer, Maj+Entrée pour retour ligne)",
      dismiss: "Fermer",
      finish: "Terminé pour l'instant",
      brainScore: "Connaissance de l'entreprise",
      brainScoreHint: "Grandit à mesure que vous partagez avec votre Brain.",
      confirmDismiss: "Fermer l'onboarding ? Vous pouvez y revenir via la bannière sur votre page d'accueil.",
      confirmComplete: "Marquer le Company Brain initial comme prêt ?",
      error: "Une erreur est survenue. Réessayez.",
      phases: {
        foundation: "Fondation",
        reality: "Réalité actuelle",
        direction: "Direction",
        measurement: "Mesure",
        assets: "Actifs",
      },
      banner: {
        startTitle: "Démarrez votre Company Brain",
        startSub: "Laissez TractionBoard vous interviewer 10 minutes pour bâtir votre mémoire d'entreprise vivante.",
        progressTitle: "Continuez votre Brain · {pct}%",
        progressSub: "Chaque réponse enseigne davantage l'IA sur votre entreprise.",
        resumeTitle: "Reprenez votre onboarding",
        start: "Démarrer",
        continue: "Continuer",
      },
    },
    docs: {
      title: "Docs",
      subtitle: "Plans vivants, playbooks et documents de stratégie — avec versionnage, approbation et chat Brain par doc.",
      loading: "Chargement…",
      empty: "Aucun doc pour l'instant. Créez le premier.",
      emptyBig: "Aucun document vivant pour l'instant.",
      loadStarterPack: "Charger le kit mémoire de départ",
      seedPack: "Ajouter le kit de départ",
      seedPackHint: "Crée 6 documents vivants pré-remplis (COMPANY, STRATEGY, PEOPLE, CUSTOMERS, PLAYBOOK, INTEGRATIONS). Ignore ceux qui existent déjà.",
      errorSeed: "Impossible de créer le kit de départ.",
      new: "Nouveau doc",
      newTitle: "Créer un nouveau doc",
      type: "Type",
      docTitle: "Titre",
      cancel: "Annuler",
      create: "Créer",
      creating: "Création…",
      selectHint: "Sélectionnez un document dans la barre latérale, ou créez-en un nouveau.",
      save: "Enregistrer",
      saving: "Enregistrement…",
      archive: "Archiver",
      archiveConfirm: "Archiver ce document ? Vous pourrez toujours le voir dans l'audit.",
      publish: "Publier comme officiel",
      versionNotePlaceholder: "Note de version (optionnel) — qu'est-ce qui a changé ?",
      versionHistory: "Historique des versions",
      restore: "Restaurer",
      restoreConfirm: "Charger cette version dans l'éditeur ? Enregistrez pour la conserver.",
      chatBtn: "Interroger ce doc",
      chatTitle: "Poser des questions sur ce document",
      chatPlaceholder: "ex. Quelle est l'étoile polaire pour Q1 ?",
      ask: "Demander",
      signoffBtn: "Approbations",
      signoffTitle: "Approbations",
      signoffIntro: "Choisissez les cofondateurs qui doivent approuver avant que ce doc soit officiel.",
      requestSignoff: "Demander les approbations",
      signedBy: "Approuvé par",
      signCommentPh: "Commentaire optionnel…",
      signNow: "Approuver maintenant",
      signedCount: "{signed}/{total} approuvé",
      statusDraft: "Brouillon",
      statusOfficial: "Officiel",
      errorCreate: "Impossible de créer le doc.",
      errorSave: "Impossible d'enregistrer.",
      errorPublish: "Impossible de publier pour l'instant.",
      errorSignoff: "L'approbation a échoué.",
      errorChat: "Le chat a échoué.",
      types: {
        "90_day": "Plan 90 jours",
        "1_year": "Plan 1 an",
        gtm: "Plan GTM",
        playbook: "Playbook",
        strategy: "Stratégie",
        prd: "PRD",
        custom: "Personnalisé",
      },
      cofounder: "Cofondateur",
      delete: "Supprimer",
      deleteConfirm: "Supprimer ce doc définitivement ? Cette action est irréversible.",
      tip: {
        version: "Historique des versions — cliquez pour voir ou restaurer une version antérieure.",
        askDoc: "Posez une question IA à partir du contenu de ce doc.",
        signoffs: "Choisissez qui doit approuver avant que ce doc devienne officiel.",
        archive: "Masquer ce doc de la barre latérale (récupérable).",
        delete: "Supprimer ce doc de manière permanente.",
        publish: "Publier comme officiel une fois toutes les approbations reçues.",
        save: "Enregistrer vos changements et incrémenter la version.",
        help: "Comment fonctionnent les Living Docs",
      },
      help: {
        title: "Comment fonctionnent les Living Docs",
        pillsTitle: "Statuts & actions",
        catsTitle: "Catégories de doc",
        vettingIntro: "Les cofondateurs (définis sur la page Équipe) sont automatiquement ajoutés comme approbateurs requis sur chaque nouveau doc. Vous pouvez ajouter ou retirer des relecteurs par doc via le panneau Approbations.",
        pill: {
          draft: "Le doc est un brouillon et peut être modifié librement.",
          official: "Toutes les approbations requises sont en place ; le doc est la source de vérité actuelle.",
          pending: "Le doc attend une ou plusieurs approbations avant d'être publié comme officiel.",
          version: "Chaque enregistrement incrémente la version. Historique complet préservé et restaurable.",
          askDoc: "Posez une question IA — la réponse s'appuie sur le contenu du doc.",
          signoffs: "Sélectionnez les personnes qui doivent approuver avant publication.",
          archive: "Masque le doc de la barre latérale. Récupérable depuis l'audit.",
          delete: "Supprime le doc définitivement. Propriétaire et admin uniquement.",
          publish: "Marque le doc comme officiel. Bloqué tant que toutes les approbations ne sont pas complètes.",
          save: "Enregistre vos modifications et crée une nouvelle version.",
        },
        cat: {
          "90_day": "Plan trimestriel : ce que votre équipe s'engage à livrer dans les 90 prochains jours.",
          "1_year": "Plan annuel : jalons, revenus, embauches, paris produit pour l'année.",
          gtm: "Playbook go-to-market : ICP, positionnement, canaux, prix, funnel.",
          playbook: "Recettes opérationnelles réutilisables : onboarding, ventes, support, incidents.",
          strategy: "Stratégie long terme : mission, moat, positionnement, catégorie, distribution.",
          prd: "Document d'exigences produit pour une fonctionnalité ou une ligne de produit.",
          custom: "Document libre — notes de réunion, mémos, ou tout ce qui n'entre pas ailleurs.",
        },
      },
    },
    legal: {
      centerTitle: "Centre juridique",
      centerSubtitle: "Toutes les ententes et politiques qui régissent TractionBoard. Versions et dates de mise à jour ci-dessous.",
      loading: "Chargement…",
      required: "Requis à l'inscription",
      versionLabel: "Version",
      effectiveDate: "Date d'entrée en vigueur",
      updated: "Dernière mise à jour",
      jurisdiction: "Juridiction",
      backToCenter: "Retour au centre juridique",
    },

  },
};

export function t(lang, path) {
  const parts = path.split(".");
  let node = dict[lang] || dict.en;
  for (const p of parts) {
    if (node && p in node) node = node[p];
    else return path;
  }
  return node;
}
